import { injectable } from 'inversify'

export const Locales = ['en', 'sl'] as const
export type Locale = (typeof Locales)[number]

export type LocaleOption = {
  code: Locale
  /** Endonym — a language is always named in its own language in a switcher. */
  label: string
  /** Emoji flag shown next to the code in the switcher. */
  flag: string
}

/**
 * Declarative half of i18n, separated from I18nRepository the same way
 * RoutesConfig is separated from RouterRepository and NavigationConfig from
 * NavigationRepository: the Repository owns *which* locale is active, this
 * owns *what locales exist and what they say*.
 *
 * Being injectable (rather than a bare module import) is the point — a test or
 * a future lazy-loading variant can bind a different table without touching
 * anything that reads it.
 */
@injectable()
export class TranslationsConfig {
  readonly fallbackLocale: Locale = 'en'

  readonly locales: LocaleOption[] = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'sl', label: 'Slovenščina', flag: '🇸🇮' },
  ]

  /**
   * Flat key -> string per locale. `{{placeholder}}` tokens are interpolated by
   * I18nRepository.t().
   *
   * This is the app's entire user-facing copy: every string a Presenter puts
   * into a ViewModel resolves through here, and no Component holds copy of its
   * own (§4.4). Keys are namespaced by the surface that owns them, so an
   * unused key is findable by grepping its namespace.
   *
   * Two deliberate exclusions:
   * - Locale endonyms below (`English`, `Slovenščina`) — a language is always
   *   named in its own language, so translating them would be wrong.
   * - `ErrorBoundaryComponent`'s copy. It is dependency-free on purpose so it
   *   still renders when the container itself is what broke; reaching into DI
   *   for a translation would defeat that.
   */
  readonly translations: Record<Locale, Record<string, string>> = {
    en: {
      'common.dismiss': 'Dismiss',
      'common.reload': 'Reload',
      'common.retry': 'Retry',
      'common.loading': 'Loading…',
      'common.confirm': 'Confirm',
      'common.close': 'Close',
      'common.cancel': 'Cancel',
      'common.failure.temporarilyUnavailable': 'The service is temporarily unavailable. Please try again.',
      'common.failure.offline': "You're offline — this change wasn't saved.",
      'common.failure.invalidExternalData':
        'The service returned data this version of the app cannot use. Please reload or try again later.',
      // Only reachable when the server rejects input the client already
      // validated as correct — a real bug (schema drift, a bypassed client),
      // never something the user themselves can fix by re-typing anything.
      'common.failure.unexpectedValidationMismatch':
        'Something unexpected went wrong on our end. Please contact an administrator.',

      'app.sessionExpired': 'Your session expired — please log in again.',
      'app.untitledPage': 'Untitled page',
      'app.pageLoadFailed': 'This page failed to load. Check your connection and try again.',

      'navigation.leave': 'Leave',
      'navigation.stay': 'Stay',
      'navigation.unsavedChanges': 'You have unsaved changes. Leave this page and lose them?',

      'auth.login': 'Login',
      'auth.register': 'Register',
      'auth.logout': 'Logout',
      'auth.guestButton': 'Enter as Guest',
      'auth.guestName': 'Guest',
      'auth.guestNameLabel': 'Your name',
      'auth.heading': 'Board Game Arena',
      'auth.subheading': 'Sign in to store your account data while using the app.',
      'auth.emailLabel': 'Email address',
      'auth.passwordLabel': 'Password',
      'auth.emailPlaceholder': 'Email',
      'auth.passwordPlaceholder': 'Password',
      'auth.inviteCodeLabel': 'Invite code',
      'auth.inviteCodePlaceholder': 'Invite code',
      'auth.logoAlt': 'logo',
      'auth.accountMenu': 'Account menu',
      'auth.loggedIn': 'User logged in',
      'auth.registered': 'User registered',
      'auth.guestSignedIn': 'Signed in as guest',

      'validation.required': '{{field}} is required.',
      'validation.tooShort': '{{field}} must be at least {{min}} characters.',
      'validation.tooLong': '{{field}} must be at most {{max}} characters.',
      'validation.invalidFormat': '{{field}} is not in a valid format.',
      'validation.invalidValue': '{{field}} is not one of the allowed values.',
      'validation.invalid': '{{field}} is not valid.',

      'principal.self': 'You',
      'principal.user': 'User {{email}}',
      'principal.team': 'Team {{name}}',

      'filters.search': 'Search',
      'filters.any': 'Any',
      'filters.sortBy': 'Sort by',
      'filters.ascending': 'A→Z',
      'filters.descending': 'Z→A',
      'filters.clear': 'Clear filters',
      'filters.summary': 'Showing {{shown}} of {{total}}',
      'filters.totalCount': '{{total}} total',
      'filters.remove': 'Remove this filter',
      'filters.noMatches': 'Nothing matches those filters.',
      'filters.sort.name': 'Name',
      'filters.sort.workspace': 'Workspace',
      'filters.sort.fewestFirst': 'Fewest first',
      'filters.sort.mostFirst': 'Most first',
      // What the *next* click on a column header will do. Read out after the
      // column's own name, so a screen reader announces "Name, sorted ascending,
      // reverse the order" rather than leaving the cycle to be discovered.
      'filters.sort.apply': 'sort by this column',
      'filters.sort.reverse': 'reverse the order',
      'filters.sort.reset': 'restore the default order',
      'filters.number.min': 'Min',
      'filters.number.max': 'Max',
      'filters.column.apply': 'Apply',
      'filters.number.atLeastChip': '{{min}}+',
      'filters.number.atMostChip': 'up to {{max}}',
      'filters.number.betweenChip': '{{min}}–{{max}}',
      'filters.date.min': 'From',
      'filters.date.max': 'To',
      'filters.date.onChip': '{{date}}',
      'filters.date.afterChip': 'from {{date}}',
      'filters.date.beforeChip': 'up to {{date}}',
      'filters.date.betweenChip': '{{min}}–{{max}}',
      'pagination.range': '{{from}}–{{to}} of {{total}}',
      'pagination.previous': 'Previous page',
      'pagination.next': 'Next page',
      'table.selectedCount': '{{count}} selected',
      'table.clearSelection': 'Clear selection',
      'table.selectRow': 'Select this row',
      'table.selectAll': 'Select every row shown',
      'table.details': 'Details',
      'table.detailsTitle': 'Details: {{name}}',
      'table.moreActions': 'More actions',
      'table.pinActions': 'Pin actions',
      'table.unpinActions': 'Unpin actions',

      // Asked when a half-typed form is about to be abandoned by something
      // smaller than a navigation — closing the pane, or opening another row's.
      'drafts.confirmDiscard': 'Discard your unsaved changes?',
      'drafts.discard': 'Discard',
      'drafts.keepEditing': 'Keep editing',
      'filters.assets.searchPlaceholder': 'Name, workspace or owner',
      'filters.assets.ownership': 'Ownership',
      'filters.assets.ownershipMine': 'Mine',
      'filters.assets.ownershipShared': 'Shared with me',
      'filters.assets.shareableOnly': 'Only ones I can share',
      'filters.grants.searchPlaceholder': 'Resource or owner',
      'filters.grants.namePlaceholder': 'Resource name',
      'filters.grants.type': 'Type',
      'filters.grants.grantedOnly': 'Only ones with grants',
      'filters.audit.searchPlaceholder': 'Who, what or detail',
      'filters.audit.actorPlaceholder': 'Actor email',
      'filters.audit.area': 'Area',
      'filters.audit.areaGrants': 'Access',
      'filters.audit.areaUsers': 'Users',
      'filters.audit.areaTeams': 'Teams',
      'filters.audit.areaWorkspaces': 'Workspaces',
      'filters.audit.areaAssets': 'Assets',
      'filters.audit.when': 'Time',
      'filters.audit.actor': 'Who',
      'filters.audit.newest': 'Newest first',
      'filters.audit.oldest': 'Oldest first',
      'filters.users.searchPlaceholder': 'Email',
      'filters.users.role': 'Role',
      'filters.users.deletedOnly': 'Only deleted',
      'filters.users.email': 'Email',
      'filters.users.content': 'Content',
      'filters.workspaces.searchPlaceholder': 'Name or owner',
      'filters.workspaces.mineOnly': 'Only mine',
      'filters.workspaces.contents': 'Contents',
      'filters.teams.searchPlaceholder': 'Name or member',
      'filters.teams.ownedOnly': 'Only ones I own',
      'filters.teams.unownedOnly': 'Only ownerless',
      'filters.teams.members': 'Members',
      'lobby.title': 'The Duke',
      'lobby.heading': 'Play The Duke',
      'lobby.description': 'Create a new game and share the code, or join one someone shared with you.',
      'lobby.createHeading': 'Start a game',
      'lobby.timeControlLabel': 'Time control',
      'lobby.timeControl.none': 'None',
      'lobby.timeControl.5': '5 minutes',
      'lobby.timeControl.10': '10 minutes',
      'lobby.timeControl.15+10': '15 minutes + 10s/move',
      'lobby.createButton': 'Create game',
      'lobby.joinHeading': 'Join a game',
      'lobby.joinCodeLabel': 'Game code',
      'lobby.joinCodePlaceholder': 'ABC123',
      'lobby.joinButton': 'Join game',
      'lobby.codeLabel': 'Game code',
      'lobby.waitingForOpponent': 'Waiting for an opponent to join…',
      'lobby.ready': 'Both players are in — ready to set up the board.',
      'lobby.hostLabel': 'Host',
      'lobby.opponentLabel': 'Opponent',
      'lobby.leaveButton': 'Leave this game',
      'lobby.invalidCode': 'Enter the 6-character game code your opponent shared.',
      'lobby.gameNotFound': 'No game was found with that code.',
      'lobby.gameFull': 'That game already has two players.',
      'lobby.gameCreated': 'Game created.',
      'lobby.gameJoined': 'Joined the game.',
      'lobby.goToGame': 'Set up the board',

      'game.setup.title': 'Set up the board',
      'game.setup.heading': 'Set up your side',
      'game.setup.description': 'Place your Duke, then two Footmen beside it.',
      'game.codeLabel': 'Game code',
      'game.seat.host': 'Host',
      'game.seat.guest': 'Guest',
      // Both seats' boards are oriented so their own home row renders at the
      // bottom (`features/game/board-orientation.ts`), so this text no
      // longer differs by seat — the two keys stay distinct in case a future
      // change (e.g. an unflipped spectator view) needs them to diverge again.
      'game.homeRow.host': 'Your home row is highlighted below.',
      'game.homeRow.guest': 'Your home row is highlighted below.',
      'game.tile.duke': 'Duke',
      'game.tile.footman': 'Footman',
      'game.tile.pikeman': 'Pikeman',
      'game.tile.bowman': 'Bowman',
      'game.tile.champion': 'Champion',
      'game.tile.general': 'General',
      'game.tile.knight': 'Knight',
      'game.tile.marshall': 'Marshall',
      'game.tile.priest': 'Priest',
      'game.tile.seer': 'Seer',
      'game.tile.wizard': 'Wizard',
      'game.tile.assassin': 'Assassin',
      'game.tile.dragoon': 'Dragoon',
      'game.tile.longbowman': 'Longbowman',
      'game.tile.ranger': 'Ranger',
      'game.setup.placeDuke': 'Place your Duke on one of the highlighted squares.',
      'game.setup.placeFootmen': 'Place two Footmen beside your Duke.',
      'game.setup.sideReady': 'Your side is set.',
      'game.setup.submitButton': 'Confirm placement',
      'game.setup.undoButton': 'Undo',
      'game.setup.clearButton': 'Clear',
      'game.setup.yourTurnToPlace': 'Place your tiles below.',
      'game.setup.waitingForOpponent': "Waiting for your opponent's setup…",
      'game.setup.opponentReady': 'Your opponent is ready.',
      'game.setup.bothReady': 'Both sides are ready — the game begins.',
      'game.setup.spectator': "You are watching this game — you don't hold a seat in it.",
      'game.setup.squareAria': 'Row {{row}}, column {{col}}{{tile}}',
      'game.setup.illegalSquare': "That square isn't a legal placement.",
      'game.setup.rosterHeading': 'Your tiles',
      'game.loadFailed': "Couldn't load this game. {{message}}",

      'game.play.title': 'Play',
      'game.play.tip': 'Move a tile to a highlighted square to make your move.',
      'game.play.dismissTip': 'Got it',
      'game.play.selectTile': 'Select one of your tiles to move.',
      'game.play.selectTileOrDraw': 'Select one of your tiles to move, or draw a new tile.',
      'game.play.selectTarget': 'Select a highlighted square to move there.',
      'game.play.opponentTurn': "Waiting for your opponent's move…",
      'game.play.spectator': "You are watching this game — you don't hold a seat in it.",
      'game.play.youWon': 'You captured the opposing Duke — you win!',
      'game.play.youLost': 'Your Duke was captured — you lose.',
      'game.play.cancelButton': 'Cancel selection',
      'game.play.currentFace': 'Current face',
      'game.play.drawnTileName': 'Drawn tile: {{name}}',
      'game.play.nextFace': 'After this move',
      'game.play.otherSide': 'Other side',
      'game.play.selectCommandDestination': 'Select where to send the commanded tile.',
      'game.play.selectDrawSquare': 'Place it on an empty square beside your Duke.',
      'game.play.drawButton': 'Draw a tile',
      'game.play.bagCount': 'Bag: {{count}}',
      'game.play.inCheck': 'Your Duke is in check.',
      'game.play.opponentInCheck': "Your opponent's Duke is in check.",
      'game.play.checkmate': 'Checkmate — your Duke has no escape.',
      'game.play.noLegalMoves': 'You have no legal moves.',

      'games.title': 'Games',
      'games.loading': 'Loading…',
      'games.newGame': 'New game',
      'games.empty': 'No games yet — start one to play.',
      'games.row.vs': 'vs {{opponent}}',
      'games.status.yourTurn': 'Your turn',
      'games.status.opponentTurn': "Opponent's turn",
      'games.status.waiting': 'Waiting for opponent',
      'games.status.finished': 'Finished',

      'books.title': 'Books',
      'books.empty': 'No books yet.',
      'books.loading': 'Loading books',
      'books.sharedBy': 'Shared by {{owner}}',
      'books.description': 'Demo reading list, seeded server-side.',
      'books.loadFailed': "Couldn't load books. {{message}}",

      'assets.added': 'Asset added',
      'assets.addFailed': "Couldn't add asset. {{message}}",
      'assets.title': 'Assets',
      'assets.empty': 'No assets yet.',
      'assets.loading': 'Loading assets',
      'assets.add': 'Add',
      'assets.namePlaceholder': 'Asset name',
      'assets.sharedBy': 'Shared by {{owner}}',
      'assets.personalWorkspace': 'My workspace',
      'assets.description': 'Equipment you own or have been given access to.',
      'assets.nameLabel': 'Name',
      'assets.workspaceLabel': 'Workspace',
      'assets.inWorkspace': 'in {{name}}',
      'assets.emptyHint': 'Add one to get started.',
      'assets.rowShare': 'Share',
      'assets.rowRename': 'Rename',
      'assets.renameTitle': 'Rename {{name}}',
      'assets.rowDelete': 'Delete',
      'assets.saveRename': 'Save',
      'assets.cancel': 'Cancel',
      'assets.renamed': 'Asset renamed.',
      'assets.deleted': 'Asset deleted.',
      'assets.confirmDelete': 'Delete {{name}}? Any access granted on it goes with it.',
      'assets.confirmDeleteMany': 'Delete {{count}} assets? Any access granted on them goes with them.',
      'sharing.title': 'Who can reach this',
      'sharing.paneTitle': 'Sharing {{name}}',
      'sharing.hint': 'Everyone listed here can see it. Read is always included.',
      'sharing.hintWorkspace': 'Everyone listed here can reach everything inside it. Read is always included.',
      'sharing.loading': 'Loading who it is shared with',
      'sharing.none': 'Not shared with anyone yet.',
      'sharing.kindUser': 'A person',
      'sharing.kindTeam': 'A team',
      'sharing.kindLink': 'An email link',
      'sharing.linkSent': 'Share link sent.',
      'sharing.emailLabel': 'Their email',
      'sharing.emailPlaceholder': 'name@example.com',
      'sharing.teamLabel': 'Team',
      'sharing.noTeams': 'You are not in a team yet — create one on the Teams page.',
      'sharing.permRead': 'See it',
      'sharing.permUpdate': 'Edit',
      'sharing.permVerify': 'Verify completed work',
      'sharing.permDelete': 'Delete',
      'sharing.permManage': 'Share further',
      'sharing.permAdd': 'Add to',
      'sharing.expiryLabel': 'Expires',
      'sharing.expiryNever': 'Never',
      'sharing.expiryDays': '{{days}} days',
      'sharing.expiresOn': 'Expires {{date}}',
      'sharing.expiredOn': 'Expired {{date}}',
      'sharing.submit': 'Share',
      'sharing.shared': 'Access granted.',
      'sharing.revoke': 'Stop sharing',
      'sharing.revoked': 'Access revoked.',
      'sharing.close': 'Done',
      'sharing.targets.open': 'Teams & members',
      'sharing.targets.heading': 'Drag a job here to share it',
      'sharing.targets.dropping': 'Drop "{{title}}" on someone',
      'sharing.targets.showOffline': 'Show offline',
      'sharing.targets.empty': 'You are not in a team yet — create one on the Teams page.',
      'sharing.targets.emptyMembers': 'No members to show',
      'sharing.targets.owner': 'Owner',
      'sharing.targets.member': 'Member',
      'sharing.targets.online': 'Online',
      'sharing.targets.offline': 'Offline',
      'sharing.targets.sharedWith': 'Shared with {{name}}.',
      'sharing.targets.alreadyShared': '{{name}} already has this.',
      'sharing.targets.alreadyTaken': 'Already shared',
      'sharing.targets.dropzone': 'Drop "{{title}}" here to share',
      'sharing.targets.dropzoneIdle': 'Hover to share',
      'sharing.targets.externalHeading': 'External collaborator',
      'sharing.targets.externalHint': 'Drag a job here first to send an outsider a link to it.',
      'sharing.targets.externalEmailLabel': 'Email',
      'sharing.targets.externalEmailPlaceholder': 'name@example.com',
      'sharing.targets.externalExpiryLabel': 'Link expires in',
      'sharing.targets.externalSend': 'Send link',
      'sharing.targets.externalSent': 'Link sent to {{email}}.',
      'inbox.title': 'Shared with you',
      'inbox.empty': 'Nothing shared with you yet.',
      'inbox.headline': '{{grantedBy}} shared "{{resourceLabel}}" with you',
      'inbox.notification': '{{grantedBy}} shared "{{resourceLabel}}" with you.',
      'inbox.reminder.headline': '"{{jobTitle}}" is coming due',
      'inbox.reminder.notification': 'Reminder: "{{jobTitle}}" is coming due.',
      'assets.move.title': 'Move it',
      'assets.move.destinationLabel': 'To workspace',
      'assets.move.submit': 'Move',
      'assets.move.moved': 'Asset moved.',
      'assets.move.noTargets': 'No other workspace you may add to.',
      'assets.showAddForm': 'Add asset',
      'assets.hideAddForm': 'Close',
      'assets.sharedHeading': 'Shared by',
      'assets.actionsHeading': 'Actions',
      'assets.detail.title': 'Asset',
      'assets.detail.loading': 'Loading asset',
      'assets.detail.notFoundTitle': 'Asset not found',
      'assets.detail.notFoundDescription': 'It may not exist, or you may not have access to it.',
      'assets.detail.backToList': 'Back to assets',
      'assets.detail.locationLabel': 'Location',
      'assets.detail.locationNone': 'No location',
      'assets.detail.locationSaved': 'Location updated.',
      'assets.detail.tabsNavLabel': 'Asset sections',
      'assets.detail.tab.overview': 'Overview',
      'assets.detail.tab.sharing': 'Sharing',
      'assets.detail.tab.activity': 'Activity',
      'assets.detail.activity.loading': 'Loading activity',
      'assets.detail.activity.empty': 'No activity recorded yet.',
      'jobs.detail.title': 'Job',
      'jobs.detail.loading': 'Loading job',
      'jobs.detail.notFoundTitle': 'Job not found',
      'jobs.detail.notFoundDescription': 'It may not exist, or you may not have access to it.',
      'jobs.detail.backToList': 'Back to jobs',
      'jobs.detail.backToShared': 'Back to shared with me',
      'jobs.detail.dueDateHeading': 'Due date',
      'jobs.detail.dueDateLabel': 'Due date',
      'jobs.detail.saveDueDate': 'Save',
      'jobs.detail.dueDateSaved': 'Due date saved.',
      'jobs.detail.instructionsHeading': 'Instructions',
      'jobs.detail.instructionsLabel': 'Instructions',
      'jobs.detail.instructionsPlaceholder': 'How to do this job...',
      'jobs.detail.saveInstructions': 'Save instructions',
      'jobs.detail.instructionsSaved': 'Instructions saved.',
      'jobs.detail.photoHeading': 'Proof of work',
      'jobs.detail.uploadPhoto': 'Upload photo',
      'jobs.detail.replacePhoto': 'Replace photo',
      'jobs.detail.removePhoto': 'Remove photo',
      'jobs.detail.photoSaved': 'Photo uploaded.',
      'jobs.detail.photoRemoved': 'Photo removed.',
      'jobs.detail.instructionPhotosHeading': 'Reference images',
      'jobs.detail.addInstructionPhoto': 'Add image',
      'jobs.detail.removeInstructionPhoto': 'Remove image',
      'jobs.detail.instructionPhotoAdded': 'Image added.',
      'jobs.detail.instructionPhotoRemoved': 'Image removed.',
      'jobs.detail.instructionPhotoLimitReached': 'Limit reached — remove one to add another.',
      'jobs.detail.procedureHeading': 'Procedure',
      'jobs.detail.procedureFieldLabel': 'Follow procedure',
      'jobs.detail.procedureNone': 'None',
      'jobs.detail.procedureChanged': 'Procedure updated.',
      'jobs.detail.procedureWillFreeze': 'A copy of this procedure freezes onto this job the moment it starts.',
      'jobs.detail.procedureFrozenAt':
        'Following the copy frozen at revision {{revision}} — edits to the procedure since then do not apply here.',
      'jobs.detail.steps.heading': 'Steps',
      'jobs.detail.steps.tick': 'Tick',
      'jobs.detail.steps.untick': 'Untick',
      'jobs.detail.steps.tickedBy': 'Ticked by {{email}}',
      'jobs.detail.steps.stepTicked': 'Step ticked.',
      'jobs.detail.steps.stepUnticked': 'Step unticked.',
      'jobs.detail.steps.addEvidence': 'Add photo',
      'jobs.detail.steps.removeEvidence': 'Remove photo',
      'jobs.detail.steps.evidenceAdded': 'Photo added.',
      'jobs.detail.steps.evidenceRemoved': 'Photo removed.',
      'jobs.detail.steps.evidenceLimitReached': 'Limit reached — remove one to add another.',
      'jobs.detail.recurrence.heading': 'Recurrence',
      'jobs.detail.recurrence.start': 'Make this job repeat',
      'jobs.detail.recurrence.stop': 'Stop repeating',
      'jobs.detail.recurrence.cleared': 'Recurrence cleared.',
      'jobs.detail.recurrence.saved': 'Recurrence saved.',
      'jobs.detail.recurrence.modeLabel': 'Trigger',
      'jobs.detail.recurrence.modeAfterCompletion': 'After completion',
      'jobs.detail.recurrence.modeFixedCalendar': 'Fixed calendar',
      'jobs.detail.recurrence.intervalLabel': 'Every (days)',
      'jobs.detail.recurrence.anchorLabel': 'Anchor date',
      'jobs.detail.recurrence.save': 'Save',

      'jobs.detail.completionNoteHeading': 'Completion note',
      'jobs.detail.completionNoteLabel': 'Issues found, or notes for next time',
      'jobs.detail.completionNotePlaceholder': 'Anything the next person should know...',
      'jobs.detail.saveCompletionNote': 'Save note',
      'jobs.detail.completionNoteSaved': 'Completion note saved.',
      'jobs.detail.review.heading': 'Review',
      'jobs.detail.review.verifiedBy': 'Verified by {{email}} on {{date}}',
      'jobs.detail.review.verificationNoteLabel': "Reviewer's findings",
      'jobs.detail.review.verificationNotePlaceholder': 'What did you check, and what did you find?',
      'jobs.detail.review.verify': 'Verify',
      'jobs.detail.review.sendBack': 'Send back',

      'nav.calendar': 'Calendar',
      'calendar.title': 'Calendar',
      'calendar.description': 'Every job with a due date, laid out by month.',
      'calendar.prevMonth': 'Previous month',
      'calendar.nextMonth': 'Next month',
      'calendar.today': 'Today',

      'admin.audit.title': 'Audit trail',
      'admin.audit.description':
        'Who changed access, and what they changed. Append-only — entries are never edited or removed.',
      'admin.audit.window': 'Showing the most recent {{limit}} entries.',
      'admin.audit.loading': 'Loading the audit trail',
      'admin.audit.empty': 'Nothing recorded yet.',
      'admin.audit.emptyHint': 'Entries appear as soon as someone changes access.',
      'admin.audit.whenHeading': 'When',
      'admin.audit.actorHeading': 'Who',
      'admin.audit.actionHeading': 'Did what',
      'admin.audit.targetHeading': 'To what',
      'admin.audit.detailHeading': 'Detail',
      'admin.audit.actionsHeading': 'Actions',
      'admin.audit.action.grant.created': 'Granted access',
      'admin.audit.action.grant.replaced': 'Changed access',
      'admin.audit.action.grant.revoked': 'Revoked access',
      'admin.audit.action.user.created': 'Created an account',
      'admin.audit.action.user.role-changed': 'Changed a role',
      'admin.audit.action.user.deleted': 'Deleted an account',
      'admin.audit.action.user.restored': 'Restored an account',
      'admin.audit.action.user.password-reset': 'Reset a password',
      'admin.audit.action.team.created': 'Created a team',
      'admin.audit.action.team.renamed': 'Renamed a team',
      'admin.audit.action.team.deleted': 'Deleted a team',
      'admin.audit.action.team.member-added': 'Added a member',
      'admin.audit.action.team.member-removed': 'Removed a member',
      'admin.audit.action.workspace.created': 'Created a workspace',
      'admin.audit.action.workspace.renamed': 'Renamed a workspace',
      'admin.audit.action.workspace.deleted': 'Deleted a workspace',
      'admin.audit.action.workspace.invite-created': 'Invited someone to a workspace',
      'admin.audit.action.workspace.invite-revoked': 'Revoked a workspace invite',
      'admin.audit.action.workspace.member-joined': 'Joined a workspace',
      'admin.audit.action.workspace.member-role-changed': "Changed a member's role",
      'admin.audit.action.workspace.member-removed': 'Removed a workspace member',
      'admin.audit.action.asset.created': 'Created an asset',
      'admin.audit.action.asset.renamed': 'Renamed an asset',
      'admin.audit.action.asset.deleted': 'Deleted an asset',
      'admin.audit.action.asset.moved': 'Moved an asset',
      'admin.audit.action.job.created': 'Created a job',
      'admin.audit.action.job.renamed': 'Renamed a job',
      'admin.audit.action.job.status-changed': "Changed a job's status",
      'admin.audit.action.job.deleted': 'Deleted a job',
      'admin.audit.action.job.moved': 'Moved a job',
      'admin.audit.kind.user': 'User',
      'admin.audit.kind.team': 'Team',
      'admin.audit.kind.workspace': 'Workspace',
      'admin.audit.kind.asset': 'Asset',
      'admin.audit.kind.book': 'Book',
      'admin.audit.kind.job': 'Job',
      'admin.audit.kind.grant': 'Grant',
      'admin.audit.kind.procedure': 'Procedure',
      'admin.audit.kind.location': 'Location',
      'admin.audit.kind.taskType': 'Task type',
      'admin.errors.title': 'Error lookup',
      'admin.errors.description': 'Errors the server recorded, with the detail a caller never sees.',
      'admin.errors.lookupTitle': 'Look up by reference',
      'admin.errors.lookupDescription': 'The reference a user was shown with their error.',
      'admin.errors.requestIdLabel': 'Reference',
      'admin.errors.requestIdPlaceholder': 'Request ID',
      'admin.errors.search': 'Look up',
      'admin.errors.recentTitle': 'Recent errors',
      'admin.errors.recentEmpty': 'No recorded errors.',
      'admin.errors.reload': 'Reload',

      'admin.users.title': 'Users',
      'admin.users.description': 'Accounts, their roles, and the data a deleted account left behind.',
      'admin.users.listTitle': 'All accounts',
      'admin.users.userHeading': 'User',
      'admin.users.statusHeading': 'Status',
      // The list could already be sorted by how much an account holds, with the
      // quantity itself nowhere on the page. A sort key with no column is an
      // order the reader cannot check.
      'admin.users.contentHeading': 'Content',
      'admin.users.contentCount': '{{assets}} assets, {{books}} books',
      'admin.users.actionsHeading': 'Actions',
      'admin.users.passwordLabel': 'New password',
      'admin.users.showCreateForm': 'Add user',
      'admin.users.hideCreateForm': 'Close',
      'admin.users.newUserEmailLabel': 'Email address',
      'admin.users.newUserEmailPlaceholder': 'person@example.com',
      'admin.users.newUserPasswordLabel': 'Password',
      'admin.users.newUserPasswordPlaceholder': 'Initial password',
      'admin.users.create': 'Create user',
      'admin.users.created': 'User created.',
      'admin.users.loading': 'Loading users',
      'admin.users.empty': 'No users.',
      'admin.users.reload': 'Reload',
      'admin.users.roleAdmin': 'Admin',
      'admin.users.roleUser': 'User',
      'admin.users.makeAdmin': 'Make admin',
      'admin.users.makeUser': 'Make user',
      'admin.users.delete': 'Delete',
      'admin.users.restore': 'Restore',
      'admin.users.resetPassword': 'Reset password',
      'admin.users.resetPasswordTitle': 'Reset password for {{email}}',
      'admin.users.newPasswordPlaceholder': 'New password',
      'admin.users.save': 'Save',
      'admin.users.cancel': 'Cancel',
      'admin.users.you': 'You',
      'admin.users.deletedAt': 'Deleted {{date}}',
      'admin.users.orphaned': 'Orphaned data: {{assets}} assets, {{books}} books',
      'admin.users.confirmDelete': 'Delete {{email}}? Their data stays and the account can be restored.',
      'admin.users.confirmDeleteMany': 'Delete {{count}} accounts? Their data stays and each account can be restored.',
      'admin.users.roleChanged': 'Role updated.',
      'admin.users.deleted': 'User deleted — their data is kept.',
      'admin.users.restored': 'User restored.',
      'admin.users.passwordReset': 'Password reset.',

      'teams.description': 'Groups of people you can share with. Creating one puts you in it.',
      'teams.listTitle': 'Teams you own or belong to',
      'teams.nameLabel': 'Name',
      'teams.ownerHeading': 'Owner',
      'teams.membersHeading': 'Members',
      'teams.actionsHeading': 'Actions',
      'teams.emailLabel': 'Member email',
      'teams.createHint': 'You become the owner and the first member.',
      'teams.ownerBadge': 'Owner',
      'teams.memberCount': '{{count}} members',
      'teams.emptyHint': 'Create one to start sharing.',
      'teams.showCreateForm': 'New team',
      'teams.hideCreateForm': 'Close',
      'teams.title': 'My teams',
      'teams.scopeMine': 'My teams',
      'teams.scopeAdmin': 'All teams',
      'teams.loading': 'Loading teams',
      'teams.empty': 'You are not in any team yet.',
      'teams.namePlaceholder': 'Team name',
      'teams.create': 'Create team',
      'teams.ownedByMe': 'You own this team',
      'teams.ownedBy': 'Owned by {{owner}}',
      'teams.noOwner': 'an administrator',
      'teams.addMember': 'Add member',
      'teams.addMemberTitle': 'Add someone to {{name}}',
      'teams.emailPlaceholder': 'User email',
      'teams.saveMember': 'Add',
      'teams.cancel': 'Cancel',
      'teams.remove': 'Remove',
      'teams.leave': 'Leave',
      'teams.makeOwner': 'Make owner',
      'teams.ownerChanged': 'Ownership transferred.',
      'teams.confirmMakeOwner': 'Hand {{name}} to {{email}}? Only they will be able to administer it afterwards.',
      'teams.delete': 'Delete',
      'teams.rename': 'Rename',
      'teams.renameTitle': 'Rename {{name}}',
      'teams.saveRename': 'Save',
      'teams.noMembers': 'No members.',
      'teams.created': 'Team created.',
      'teams.deleted': 'Team deleted.',
      'teams.renamed': 'Team renamed.',
      'teams.memberAdded': 'Member added.',
      'teams.memberRemoved': 'Member removed.',
      'teams.left': 'You left the team.',
      'teams.confirmDelete': 'Delete team {{name}}? Its access grants are revoked with it.',
      'teams.confirmDeleteMany': 'Delete {{count}} teams? Their access grants are revoked with them.',

      'settings.title': 'Settings',
      'settings.description': 'How and when you want to be reminded about jobs coming due.',
      'settings.accountTitle': 'Account',
      'settings.displayNameLabel': 'Display name',
      'settings.displayNamePlaceholder': 'The name others see instead of your email',
      'settings.saveNameLabel': 'Save name',
      'settings.nameSaved': 'Display name saved.',
      'settings.currentPasswordLabel': 'Current password',
      'settings.newPasswordLabel': 'New password',
      'settings.changePasswordLabel': 'Change password',
      'settings.passwordChanged': 'Password changed.',
      'settings.enabledLabel': 'Send me reminders',
      'settings.leadDaysLabel': 'Remind me',
      'settings.leadDays.dayOf': 'On the day it is due',
      'settings.leadDays.days': '{{count}} days before it is due',
      'settings.jobSetLabel': 'Which jobs',
      'settings.jobSet.assigned': 'Only jobs assigned or claimed by me',
      'settings.jobSet.involved': 'Jobs assigned to me or in my own workspace',
      'settings.jobSet.visible': 'Every job I can see',
      'settings.hourOfDayLabel': 'Hour of day (0-23)',
      'settings.timeZoneLabel': 'Time zone',
      'settings.emailLabel': 'Email me',
      'settings.inAppLabel': 'Show in my inbox',
      'settings.saveLabel': 'Save',
      'settings.saved': 'Preferences saved.',
      'settings.export.title': 'Export your data',
      'settings.export.description': 'Download a copy of your own jobs.',
      'settings.export.jsonLabel': 'Download JSON',
      'settings.export.csvLabel': 'Download CSV',
      'settings.export.downloaded': 'Export downloaded.',
      'settings.calendarFeed.title': 'Calendar feed',
      'settings.calendarFeed.description':
        'Subscribe in your phone or desktop calendar to see jobs coming due, without opening the app.',
      'settings.calendarFeed.copyLabel': 'Copy link',
      'settings.calendarFeed.copied': 'Link copied.',
      'settings.calendarFeed.mintLabel': 'Turn on',
      'settings.calendarFeed.regenerateLabel': 'Regenerate link',
      'settings.calendarFeed.revokeLabel': 'Turn off',
      'settings.calendarFeed.minted': 'Calendar feed ready.',
      'settings.calendarFeed.revoked': 'Calendar feed turned off.',
      'settings.push.title': 'Push notifications',
      'settings.push.description':
        'Get a notification on this device when a job is coming due, even when the app is closed.',
      'settings.push.toggleLabel': 'Notify me on this device',
      'settings.push.enableLabel': 'Turn on',
      'settings.push.disableLabel': 'Turn off',
      'settings.push.enabled': 'Push notifications on for this device.',
      'settings.push.disabled': 'Push notifications off for this device.',
      'settings.push.unsupported': 'This browser does not support push notifications.',
      'settings.push.blocked': 'Notifications are blocked in your browser settings — allow them there first.',
      'settings.push.serverUnavailable': 'Push notifications are not configured on the server.',
      'settings.push.failed': 'Could not change push notifications. Try again.',
      'workspaces.description': 'Containers your resources live in, and who else can reach them.',
      'workspaces.listTitle': 'Your workspaces',
      'workspaces.listDescription': 'Move an asset between them from its own row on the Assets page.',
      'workspaces.nameLabel': 'Name',
      'workspaces.nameHeading': 'Workspace',
      'workspaces.ownerHeading': 'Owner',
      'workspaces.contentsHeading': 'Contents',
      'workspaces.accessHeading': 'Your access',
      'workspaces.actionsHeading': 'Actions',
      'workspaces.emptyHint': 'Create one to group what you own.',
      'workspaces.showCreateForm': 'New workspace',
      'workspaces.hideCreateForm': 'Close',
      'workspaces.title': 'Workspaces',
      'workspaces.loading': 'Loading workspaces',
      'workspaces.empty': 'No workspaces.',
      'workspaces.assetCount': '{{count}} assets',
      'workspaces.personalBadge': 'Personal',
      'workspaces.perm.read': 'Read',
      'workspaces.perm.add': 'Add',
      'workspaces.perm.update': 'Update',
      'workspaces.perm.delete': 'Delete',
      'workspaces.perm.manage': 'Manage',
      'workspaces.namePlaceholder': 'Workspace name',
      'workspaces.create': 'Create',
      'workspaces.rename': 'Rename',
      'workspaces.renameTitle': 'Rename {{name}}',
      'workspaces.share': 'Share',
      'workspaces.members': 'Members',
      'workspaces.members.paneTitle': 'Members of {{name}}',
      'workspaces.members.title': 'Members',
      'workspaces.members.memberHeading': 'Member',
      'workspaces.members.roleHeading': 'Role',
      'workspaces.members.addedHeading': 'Joined',
      'workspaces.members.actionsHeading': 'Actions',
      'workspaces.members.remove': 'Remove',
      'workspaces.members.confirmRemove': 'Remove {{email}} from this workspace?',
      'workspaces.members.removed': 'Member removed.',
      'workspaces.members.roleChanged': 'Role updated.',
      'workspaces.members.inviteEmailLabel': 'Invite by email',
      'workspaces.members.inviteRoleLabel': 'As',
      'workspaces.members.inviteSubmit': 'Send invite',
      'workspaces.members.inviteSent': 'Invitation sent.',
      'workspaces.members.inviteRevoked': 'Invitation revoked.',
      'workspaces.members.pendingTitle': 'Pending invitations',
      'workspaces.members.pending': 'pending',
      'workspaces.members.revoked': 'revoked',
      'workspaces.members.expired': 'expired',
      'workspaces.members.revoke': 'Revoke',
      'workspaces.invite.title': 'Workspace invitation',
      'workspaces.invite.accepting': 'Accepting your invitation…',
      'workspaces.invite.joined': 'You have joined {{workspace}}.',
      'workspaces.invite.alreadyMember': 'You are already in {{workspace}}.',
      'workspaces.invite.invalid': 'This invitation is no longer valid',
      'workspaces.invite.invalidHelp':
        'The link may have expired, been revoked, or already been used. Ask for a new one.',
      'workspaces.invite.goHome': 'Go to the app',
      'workspaces.saveRename': 'Save',
      'workspaces.delete': 'Delete',
      'workspaces.cancel': 'Cancel',
      'workspaces.created': 'Workspace created.',
      'workspaces.renamed': 'Workspace renamed.',
      'workspaces.deleted': 'Workspace deleted.',
      'workspaces.confirmDelete': 'Delete workspace {{name}}? Its access grants are revoked with it.',
      'workspaces.confirmDeleteMany': 'Delete {{count}} workspaces? Their access grants are revoked with them.',

      'nav.jobs': 'Jobs',
      'jobs.description': 'Work that needs doing, tracked from todo through done.',
      'jobs.listTitle': 'Your jobs',
      'jobs.listDescription': 'Reference an asset from a job to say what it concerns.',
      'jobs.titleLabel': 'Title',
      'jobs.titlePlaceholder': 'Job title',
      'jobs.workspaceFieldLabel': 'Workspace',
      'jobs.assetFieldLabel': 'Asset (optional)',
      'jobs.dueOnFieldLabel': 'Due (optional)',
      'jobs.locationFieldLabel': 'Location',
      'jobs.locationDerive': 'From the asset',
      'jobs.locationNone': 'No location',
      'jobs.detail.locationLabel': 'Location',
      'jobs.taskTypeFieldLabel': 'Task type',
      'jobs.taskTypeNone': 'No type',
      'jobs.detail.taskTypeLabel': 'Task type',
      'jobs.titleHeading': 'Job',
      'jobs.statusHeading': 'Status',
      'jobs.ownerHeading': 'Owner',
      'jobs.workspaceHeading': 'Workspace',
      'jobs.taskTypeHeading': 'Task type',
      'jobs.locationHeading': 'Location',
      'jobs.assetHeading': 'Asset',
      'jobs.dueOnHeading': 'Due',
      'jobs.accessHeading': 'Your access',
      'jobs.actionsHeading': 'Actions',
      'jobs.emptyHint': 'Create one to start tracking work.',
      'jobs.showCreateForm': 'New job',
      'jobs.hideCreateForm': 'Close',
      'jobs.title': 'Jobs',
      'jobs.loading': 'Loading jobs',
      'jobs.empty': 'No jobs.',
      'jobs.status.todo': 'To do',
      'jobs.status.doing': 'Doing',
      'jobs.status.done': 'Done',
      'jobs.status.verified': 'Verified',
      'jobs.start': 'Start',
      'jobs.complete': 'Complete',
      'jobs.reopen': 'Reopen',
      'jobs.verify': 'Verify',
      'jobs.sendBack': 'Send back',
      'jobs.verified': 'Job verified.',
      'jobs.sentBack': 'Job sent back for rework.',
      'jobs.confirmSendBack': 'Send {{name}} back for rework? This clears completion and the claim.',
      'jobs.claim': 'Claim',
      'jobs.release': 'Release',
      'jobs.claimedByYou': 'Claimed by you',
      'jobs.claimedByOther': 'Claimed by {{email}}',
      'jobs.assignedToYouBy': 'Assigned to you by {{by}}',
      'jobs.assignedToOtherBy': 'Assigned to {{email}} by {{by}}',
      'jobs.claimed': 'Job claimed.',
      'jobs.claimReleased': 'Claim released.',
      'jobs.assign': 'Assign',
      'jobs.assigneeLabel': "Assignee's email",
      'jobs.assigneePlaceholder': 'name@example.com',
      'jobs.assigned': '{{email}} was assigned this job.',
      'jobs.overdueBadge': 'Overdue',
      'jobs.perm.read': 'Read',
      'jobs.perm.add': 'Add',
      'jobs.perm.update': 'Update',
      'jobs.perm.verify': 'Verify',
      'jobs.perm.delete': 'Delete',
      'jobs.perm.manage': 'Manage',
      'jobs.create': 'Create',
      'jobs.rename': 'Rename',
      'jobs.renameTitle': 'Rename {{name}}',
      'jobs.share': 'Share',
      'jobs.saveRename': 'Save',
      'jobs.delete': 'Delete',
      'jobs.cancel': 'Cancel',
      'jobs.created': 'Job created.',
      'jobs.renamed': 'Job renamed.',
      'jobs.deleted': 'Job deleted.',
      'jobs.statusChanged': 'Status updated.',
      'jobs.confirmDelete': 'Delete job {{name}}? Its access grants are revoked with it.',
      'jobs.confirmDeleteMany': 'Delete {{count}} jobs? Their access grants are revoked with them.',
      'jobs.recurringBadge': 'Recurring',
      'jobs.repeatsBadge': 'Repeats',
      'filters.jobs.searchPlaceholder': 'Search jobs…',
      'filters.jobs.status': 'Status',
      'filters.jobs.overdueOnly': 'Overdue only',
      'filters.jobs.dueOn': 'Due',
      'filters.jobs.taskType': 'Task type',
      'filters.jobs.location': 'Location',
      'filters.audit.areaJobs': 'Jobs',
      'admin.access.typeJob': 'Job',
      'admin.access.typeProcedure': 'Procedure',

      'nav.procedures': 'Procedures',
      'nav.procedure': 'Procedure',
      'nav.locations': 'Locations',
      'nav.location': 'Location',
      'locations.title': 'Locations',
      'locations.description':
        'Where work happens — Company → Factory → Hall → Line, or campsite → reception → toilets. Nest as deep as you need.',
      'locations.nameLabel': 'Name',
      'locations.namePlaceholder': 'Building, room, bay…',
      'locations.workspaceFieldLabel': 'Workspace',
      'locations.parentFieldLabel': 'Inside',
      'locations.parentNone': 'Top level',
      'locations.showCreateForm': 'New location',
      'locations.hideCreateForm': 'Close',
      'locations.loading': 'Loading locations',
      'locations.empty': 'No locations yet.',
      'locations.emptyHint': 'Add one to say where work is done.',
      'locations.expand': 'Expand',
      'locations.collapse': 'Collapse',
      'locations.create': 'Create',
      'locations.created': 'Location created.',
      'locations.confirmDelete':
        'Delete {{name}}? Its child locations move up to its parent, and any assets placed here lose their location.',
      'locations.detail.title': 'Location',
      'locations.detail.backToList': 'Back to locations',
      'locations.detail.loading': 'Loading location',
      'locations.detail.notFoundTitle': 'Location not found',
      'locations.detail.notFoundDescription': 'It may not exist, or you may not have access to it.',
      'locations.detail.workspaceLabel': 'Workspace',
      'locations.detail.nameFieldLabel': 'Name',
      'locations.detail.saveName': 'Save name',
      'locations.detail.nameSaved': 'Name saved.',
      'locations.detail.parentFieldLabel': 'Inside',
      'locations.detail.parentSaved': 'Moved.',
      'locations.detail.childrenHeading': 'Locations inside this one',
      'locations.detail.noChildren': 'Nothing nested here yet.',
      'locations.detail.assetsHereHeading': 'Assets here',
      'locations.detail.noAssetsHere': 'No assets placed at this location.',
      'locations.detail.jobsHereHeading': 'Jobs here',
      'locations.detail.noJobsHere': 'No jobs recorded at this location.',
      'locations.detail.delete': 'Delete location',
      'nav.shared': 'Shared with me',
      'shared.title': 'Shared with me',
      'shared.description': 'Jobs other people have given you access to.',
      'shared.listTitle': 'Jobs shared with you',
      'shared.loading': 'Loading shared jobs',
      'shared.empty': 'Nothing shared with you yet.',
      'shared.emptyHint': 'When someone shares a job with you, it shows up here.',
      'shared.sharedBy': 'Shared by {{owner}}',
      'nav.taskTypes': 'Task types',
      'taskTypes.title': 'Task types',
      'taskTypes.description':
        'What kind of work a task is — cleaning, inspection, repair, delivery. Use them to group and filter the task list.',
      'taskTypes.nameLabel': 'Name',
      'taskTypes.namePlaceholder': 'Cleaning, inspection…',
      'taskTypes.workspaceFieldLabel': 'Workspace',
      'taskTypes.showCreateForm': 'New task type',
      'taskTypes.hideCreateForm': 'Close',
      'taskTypes.empty': 'No task types yet.',
      'taskTypes.emptyHint': 'Add one to classify the kinds of work you do.',
      'taskTypes.create': 'Create',
      'taskTypes.created': 'Task type created.',
      'taskTypes.rename': 'Rename',
      'taskTypes.renamed': 'Task type renamed.',
      'taskTypes.saveName': 'Save',
      'taskTypes.delete': 'Delete',
      'taskTypes.deleted': 'Task type deleted.',
      'taskTypes.confirmDelete': 'Delete {{name}}? Tasks of this type keep going, they just lose the label.',
      'taskTypes.nameHeading': 'Name',
      'taskTypes.workspaceHeading': 'Workspace',
      'taskTypes.accessHeading': 'Access',
      'taskTypes.actionsHeading': 'Actions',
      'taskTypes.perm.read': 'Read',
      'taskTypes.perm.add': 'Add',
      'taskTypes.perm.update': 'Update',
      'taskTypes.perm.verify': 'Verify',
      'taskTypes.perm.delete': 'Delete',
      'taskTypes.perm.manage': 'Manage',
      'procedures.title': 'Procedures',
      'procedures.description':
        'Reusable instructions, referenced by jobs and rules — edit one place, every job that starts after your edit sees it.',
      'procedures.listTitle': 'Your procedures',
      'procedures.listDescription':
        'A job freezes its own copy the moment it starts, so later edits never change work already in progress.',
      'procedures.titleLabel': 'Title',
      'procedures.titlePlaceholder': 'What this procedure covers',
      'procedures.workspaceFieldLabel': 'Workspace',
      'procedures.bodyLabel': 'Instructions (optional)',
      'procedures.bodyPlaceholder': 'Step by step, or whatever the next person needs to know',
      'procedures.titleHeading': 'Procedure',
      'procedures.bodyHeading': 'Instructions',
      'procedures.revisionHeading': 'Revision',
      'procedures.revisionLabel': 'Rev. {{count}}',
      'procedures.workspaceHeading': 'Workspace',
      'procedures.accessHeading': 'Access',
      'procedures.actionsHeading': 'Actions',
      'procedures.showCreateForm': 'New procedure',
      'procedures.hideCreateForm': 'Close',
      'procedures.loading': 'Loading procedures',
      'procedures.empty': 'No procedures.',
      'procedures.emptyHint': 'Create one to stop re-typing the same instructions.',
      'procedures.create': 'Create',
      'procedures.created': 'Procedure created.',
      'procedures.deleted': 'Procedure deleted.',
      'procedures.delete': 'Delete',
      'procedures.confirmDelete':
        'Delete the procedure {{name}}? Jobs already following it keep their own frozen copy; jobs and rules only referencing it lose the link.',
      'procedures.perm.read': 'Read',
      'procedures.perm.add': 'Add',
      'procedures.perm.update': 'Update',
      'procedures.perm.delete': 'Delete',
      'procedures.perm.manage': 'Manage',

      'procedures.detail.title': 'Procedure',
      'procedures.detail.backToList': 'Back to procedures',
      'procedures.detail.loading': 'Loading procedure',
      'procedures.detail.notFoundTitle': 'Procedure not found',
      'procedures.detail.notFoundDescription': 'It may not exist, or you may not have access to it.',
      'procedures.detail.titleFieldLabel': 'Title',
      'procedures.detail.bodyFieldLabel': 'Intro (optional)',
      'procedures.detail.save': 'Save',
      'procedures.detail.saved': 'Procedure saved.',
      'procedures.detail.stepsHeading': 'Steps',
      'procedures.detail.addStep': 'Add step',
      'procedures.detail.stepAdded': 'Step added.',
      'procedures.detail.editStep': 'Edit',
      'procedures.detail.saveStep': 'Save',
      'procedures.detail.cancelEditStep': 'Cancel',
      'procedures.detail.stepUpdated': 'Step updated.',
      'procedures.detail.removeStep': 'Remove',
      'procedures.detail.stepRemoved': 'Step removed.',
      'procedures.detail.stepsReordered': 'Steps reordered.',
      'procedures.detail.moveUp': 'Move up',
      'procedures.detail.moveDown': 'Move down',
      'procedures.detail.addPhoto': 'Add photo',
      'procedures.detail.removePhoto': 'Remove photo',
      'procedures.detail.photoAdded': 'Photo added.',
      'procedures.detail.photoRemoved': 'Photo removed.',
      'procedures.detail.photoLimitReached': 'Limit reached — remove one to add another.',

      'admin.grants.title': 'Resources & grants',
      'admin.grants.description': 'Every resource in the system, and who has been granted access to it.',
      'admin.access.inspectDescription': 'Effective access, with inherited and team-reached grants expanded.',
      'admin.access.emailLabel': 'User email',
      'admin.access.teamLabel': 'Team',
      'admin.access.resourceLabel': 'Resource',
      'admin.access.typeHeading': 'Type',
      'admin.access.nameHeading': 'Resource',
      'admin.access.ownerHeading': 'Owner',
      'admin.access.grantsHeading': 'Grants',
      'admin.access.actionsHeading': 'Actions',
      'admin.access.loading': 'Loading resources',
      'admin.access.empty': 'No resources.',
      'admin.access.reload': 'Reload',
      'admin.access.typeAsset': 'Asset',
      'admin.access.typeBook': 'Book',
      'admin.access.typeLocation': 'Location',
      'admin.access.typeTaskType': 'Task type',
      'admin.access.typeWorkspace': 'Workspace',
      'admin.access.owner': 'Owner: {{owner}}',
      'admin.access.inWorkspace': 'In {{name}}',
      'admin.access.ownerDeleted': 'owner deleted',
      'admin.access.noGrants': 'No grants.',
      'admin.access.grant': 'Grant access',
      'admin.access.grantTitle': 'Grant access to {{name}}',
      'admin.access.revoke': 'Revoke',
      'admin.access.kindUser': 'User',
      'admin.access.kindTeam': 'Team',
      'admin.access.emailPlaceholder': 'User email',
      'admin.access.noTeamsHint': 'No teams yet — create one above.',
      'admin.access.permRead': 'Read',
      'admin.access.permAdd': 'Add',
      'admin.access.permUpdate': 'Update',
      'admin.access.permVerify': 'Verify',
      'admin.access.permDelete': 'Delete',
      'admin.access.permManage': 'Manage',
      'admin.access.saveGrant': 'Grant',
      'admin.access.cancel': 'Cancel',
      'admin.access.granted': 'Access granted.',
      'admin.access.revoked': 'Grant revoked.',
      'admin.access.inspectTitle': 'Access inspector',
      'admin.access.inspectByUser': 'By user',
      'admin.access.inspectByResource': 'By resource',
      'admin.access.inspectRun': 'Look up',
      'admin.access.inspectUserHeading': 'Access for {{email}}',
      'admin.access.inspectResourceHeading': 'Who can access {{name}}',
      'admin.access.inspectUserNotFound': 'No user with that email exists.',
      'admin.access.inspectNoRows': 'This user has no access to any resource.',
      'admin.access.deletedBadge': 'deleted',
      'admin.access.sourceOwner': 'Owner',
      'admin.access.sourceTeamOwner': 'Owned by team “{{name}}”',
      'admin.access.sourceDirect': 'Direct grant',
      'admin.access.sourceTeam': 'Via team “{{name}}”',
      'admin.access.sourceWorkspace': 'Inherited from workspace “{{name}}”',

      'nav.home': 'Home',
      'nav.games': 'Games',
      'nav.play': 'The Duke',
      'nav.activeGame': 'vs {{opponent}} — {{status}}',
      'nav.activeGame.yourTurn': 'your turn',
      'nav.activeGame.opponentTurn': "opponent's turn",
      'nav.activeGame.waiting': 'waiting',
      'nav.gameSetup': 'Set up the board',
      'nav.gamePlay': 'Play the game',
      'nav.group.library': 'Library',
      'nav.group.workspace': 'Workspace',
      'nav.group.admin': 'Administration',
      'nav.adminUsers': 'Users',
      'nav.adminAudit': 'Audit trail',
      'nav.adminInspector': 'Access inspector',
      'nav.adminGrants': 'Resources & grants',
      'nav.group.demo': 'Demo',
      'nav.group.hidden': 'Hidden',
      'nav.books': 'Books',
      'nav.assets': 'Assets',
      'nav.asset': 'Asset',
      'nav.job': 'Job',
      'nav.workspaces': 'Workspaces',
      'nav.workspaceInvite': 'Workspace invitation',
      'nav.teams': 'Teams',
      'nav.users': 'People (demo)',
      'nav.invisible': 'Invisible',
      'nav.unclickable': 'UnClickable',
      'nav.clickable': 'Clickable',
      'nav.expandable': 'Expandable Jure',
      'nav.child1': 'Child 1',
      'nav.child2': 'Child 2',
      'nav.both': 'Both',
      'nav.columns': 'Columns',
      'nav.settings': 'Settings',
      'nav.advanced': 'Advanced',
      'nav.optionA': 'Option A',
      'nav.optionB': 'Option B',
      'nav.infraDemo': 'Infra Demo',
      'nav.adminErrors': 'Error log',
      'nav.google': 'Google',
      'nav.up': 'Navigate up',
      'nav.browseUp': 'Browse up',
      'nav.backToCurrent': 'Back to current page',
      'nav.section': 'Navigate section',
      'nav.expand': 'Expand',
      'nav.collapse': 'Collapse',

      'shell.showNavigation': 'Show navigation',
      'shell.hideNavigation': 'Hide navigation',
      'shell.search': 'Search',
      'shell.breadcrumb': 'Breadcrumb',
      'shell.language': 'Language',
      'shell.adminMode': 'Admin mode',
      'shell.adminModeOn': 'Admin mode is on',
      'shell.adminModeOff': 'Admin mode is off',
      'workspace.switcher.label': 'Switch workspace',
      'workspace.role.owner': 'Owner',
      'workspace.role.manager': 'Manager',
      'workspace.role.member': 'Member',
      'workspace.role.guest': 'Guest',

      'commandPalette.pages': 'Pages',
      'commandPalette.empty': 'No results found.',
      'commandPalette.quickAccess': 'Quick access',
      'commandPalette.navigateHint': 'to navigate',
      'commandPalette.selectHint': 'to select',
      'commandPalette.closeHint': 'to close',
      'commandPalette.unread': 'Unread',
      'commandPalette.external': 'Opens in a new tab',
      'commandPalette.searchResults': 'Search results',
      'commandPalette.searching': 'Searching…',

      'theme.toggleLabel': 'Theme: {{current}} (click to switch to {{next}})',
      'theme.light': 'Light',
      'theme.dark': 'Dark',
      'theme.system': 'System',

      'connectivity.offline': "You're offline — some things may not work.",
      'connectivity.stale': 'Showing data last loaded at {{time}}.',
      'connectivity.newVersion': 'A new version is available.',

      'notFound.title': '404 — Page not found',
      'notFound.body': 'The address you entered does not match any page.',
      'notFound.goHome': 'Go Home',

      'share.link.title': 'Opening shared job',
      'share.link.exchanging': 'Opening the job that was shared with you',
      'share.link.invalid': 'This link is no longer valid',
      'share.link.invalidHelp':
        'The share link has expired or been revoked. Ask whoever shared the job to send a new one.',
      'share.link.backToLogin': 'Go to sign in',

      'routeInfo.path': 'path',
      'routeInfo.params': 'params',
      'routeInfo.query': 'query',

      'demo.title': 'Infra Demo',
      'demo.intro':
        'Playground for the cross-cutting pieces that have no natural UI trigger of their own. (Theming, mobile layout, session redirect-back, and the route progress bar — see Settings, 1s delay — already show themselves during normal use.)',

      'demo.notifications.title': 'Notifications',
      'demo.notifications.info': 'Info',
      'demo.notifications.success': 'Success',
      'demo.notifications.warning': 'Warning',
      'demo.notifications.error': 'Error',
      'demo.notifications.sticky': 'Sticky (no auto-dismiss)',
      'demo.notifications.msgInfo': 'Here is some info.',
      'demo.notifications.msgSuccess': 'That worked.',
      'demo.notifications.msgWarning': 'Careful with that.',
      'demo.notifications.msgError': 'That did not work.',
      'demo.notifications.msgSticky': 'Sticky — dismiss me manually.',

      'demo.gateways.title': 'Utility gateways',
      'demo.gateways.readClock': 'Read clock',
      'demo.gateways.generateId': 'Generate ID',
      'demo.gateways.rollRandom': 'Roll random',

      'demo.draft.title': 'Persisted draft (survives reload)',
      'demo.draft.placeholder': 'Type something, then reload the page.',

      'demo.dialog.title': 'Dialog',
      'demo.dialog.ask': 'Ask to confirm',
      'demo.dialog.lastResult': 'last result: {{result}}',
      'demo.dialog.question': 'Do the thing?',
      'demo.dialog.confirmLabel': 'Do it',
      'demo.dialog.cancelLabel': 'Never mind',
      'demo.dialog.confirmed': 'Confirmed.',
      'demo.dialog.cancelled': 'Cancelled.',

      'demo.observability.title': 'Logging & telemetry',
      'demo.observability.log': 'Log a warning',
      'demo.observability.track': 'Track an event',
      'demo.observability.logged': 'Logged — check the browser console.',
      'demo.observability.tracked': 'Tracked — check the browser console.',
      'demo.observability.note':
        'No real sink yet — both just go to the console, but every call site already goes through the gateway.',

      'demo.connectivity.title': 'Connectivity & app version',
      'demo.connectivity.goOffline': 'Simulate going offline',
      'demo.connectivity.goOnline': 'Simulate coming back online',
      'demo.connectivity.online': 'online',
      'demo.connectivity.offline': 'offline',
      'demo.connectivity.simulateVersion': 'Simulate new version available',
      'demo.connectivity.version': 'v{{version}}',
      'demo.connectivity.versionUpdate': 'v{{version}} (update available — see banner)',

      'demo.i18n.title': 'i18n & formatting',
      'demo.i18n.switchLocale': 'Switch locale',

      'demo.scopedMessage.title': 'Scoped message (info / warning)',
      'demo.scopedMessage.info': 'Show info',
      'demo.scopedMessage.warning': 'Show warning',
      'demo.scopedMessage.result': 'Show an action result',
      'demo.scopedMessage.clear': 'Clear',
      'demo.scopedMessage.msgInfo': 'This list is filtered — 3 of 12 shown.',
      'demo.scopedMessage.msgWarning': 'You have unsaved changes on this page.',
      'demo.scopedMessage.msgFailed': 'The server refused that.',
      'demo.scopedMessage.note':
        'info and warning are placed explicitly through show(), because nothing happened that a tone could be derived from. success and error can only ever come from an action result — the third button goes through the derived path to show the difference.',

      'demo.unsaved.title': 'Unsaved work warning',
      'demo.unsaved.placeholder': 'Type here, then try reloading the page.',
      'demo.unsaved.save': 'Save',
      'demo.unsaved.dirty': 'unsaved',
      'demo.unsaved.clean': 'saved',
      'demo.unsaved.note':
        'While this differs from the saved value, reloading or closing the tab makes the browser ask first — the only exit the platform lets an app intervene in. Navigating away in the app, or using Back, is allowed: the note lives in a Repository, so it is still here when you return.',
      'demo.crash.title': 'Crash boundary',
      'demo.crash.button': 'Crash this page',

      greeting: 'Hello, {{name}}!',
      itemCount: '{{count}} items',
    },
    sl: {
      'common.dismiss': 'Opusti',
      'common.reload': 'Osveži',
      'common.retry': 'Poskusi znova',
      'common.loading': 'Nalaganje…',
      'common.confirm': 'Potrdi',
      'common.close': 'Zapri',
      'common.cancel': 'Prekliči',
      'common.failure.temporarilyUnavailable': 'Storitev trenutno ni na voljo. Poskusite znova.',
      'common.failure.offline': 'Niste povezani — sprememba ni bila shranjena.',
      'common.failure.invalidExternalData':
        'Storitev je vrnila podatke, ki jih ta različica aplikacije ne more uporabiti. Osvežite stran ali poskusite pozneje.',
      'common.failure.unexpectedValidationMismatch':
        'Prišlo je do nepričakovane napake. Prosimo, obrnite se na skrbnika.',

      'app.sessionExpired': 'Seja je potekla — prijavite se znova.',
      'app.untitledPage': 'Neimenovana stran',
      'app.pageLoadFailed': 'Strani ni bilo mogoče naložiti. Preverite povezavo in poskusite znova.',

      'navigation.leave': 'Zapusti',
      'navigation.stay': 'Ostani',
      'navigation.unsavedChanges': 'Imate neshranjene spremembe. Želite zapustiti stran in jih izgubiti?',

      'auth.login': 'Prijava',
      'auth.register': 'Registracija',
      'auth.logout': 'Odjava',
      'auth.guestButton': 'Vstopi kot gost',
      'auth.guestName': 'Gost',
      'auth.guestNameLabel': 'Vaše ime',
      'auth.heading': 'Board Game Arena',
      'auth.subheading': 'Prijavite se, da se podatki vašega računa shranijo med uporabo aplikacije.',
      'auth.emailLabel': 'E-poštni naslov',
      'auth.passwordLabel': 'Geslo',
      'auth.emailPlaceholder': 'E-pošta',
      'auth.passwordPlaceholder': 'Geslo',
      'auth.inviteCodeLabel': 'Koda povabila',
      'auth.inviteCodePlaceholder': 'Koda povabila',
      'auth.logoAlt': 'logotip',
      'auth.accountMenu': 'Meni računa',
      'auth.loggedIn': 'Uporabnik je prijavljen',
      'auth.registered': 'Uporabnik je registriran',
      'auth.guestSignedIn': 'Prijavljeni kot gost',

      'validation.required': '{{field}} je obvezno polje.',
      'validation.tooShort': '{{field}} mora imeti vsaj {{min}} znakov.',
      'validation.tooLong': '{{field}} lahko ima največ {{max}} znakov.',
      'validation.invalidFormat': '{{field}} ni v veljavni obliki.',
      'validation.invalidValue': '{{field}} ni ena od dovoljenih vrednosti.',
      'validation.invalid': '{{field}} ni veljavno.',

      'principal.self': 'Vi',
      'principal.user': 'Uporabnik {{email}}',
      'principal.team': 'Ekipa {{name}}',

      'filters.search': 'Iskanje',
      'filters.any': 'Vse',
      'filters.sortBy': 'Razvrsti po',
      'filters.ascending': 'A→Ž',
      'filters.descending': 'Ž→A',
      'filters.clear': 'Počisti filtre',
      'filters.summary': 'Prikazanih {{shown}} od {{total}}',
      'filters.totalCount': 'Skupaj {{total}}',
      'filters.remove': 'Odstrani ta filter',
      'filters.noMatches': 'Nič ne ustreza tem filtrom.',
      'filters.sort.name': 'Ime',
      'filters.sort.workspace': 'Delovni prostor',
      'filters.sort.fewestFirst': 'Najmanj najprej',
      'filters.sort.mostFirst': 'Največ najprej',
      'filters.sort.apply': 'razvrsti po tem stolpcu',
      'filters.sort.reverse': 'obrni vrstni red',
      'filters.sort.reset': 'ponastavi privzeti vrstni red',
      'filters.number.min': 'Min',
      'filters.number.max': 'Maks',
      'filters.column.apply': 'Uporabi',
      'filters.number.atLeastChip': '{{min}}+',
      'filters.number.atMostChip': 'do {{max}}',
      'filters.number.betweenChip': '{{min}}–{{max}}',
      'filters.date.min': 'Od',
      'filters.date.max': 'Do',
      'filters.date.onChip': '{{date}}',
      'filters.date.afterChip': 'od {{date}}',
      'filters.date.beforeChip': 'do {{date}}',
      'filters.date.betweenChip': '{{min}}–{{max}}',
      'pagination.range': '{{from}}–{{to}} od {{total}}',
      'pagination.previous': 'Prejšnja stran',
      'pagination.next': 'Naslednja stran',
      'table.selectedCount': 'Izbranih: {{count}}',
      'table.clearSelection': 'Počisti izbor',
      'table.selectRow': 'Izberi to vrstico',
      'table.selectAll': 'Izberi vse prikazane vrstice',
      'table.details': 'Podrobnosti',
      'table.detailsTitle': 'Podrobnosti: {{name}}',
      'table.moreActions': 'Več dejanj',
      'table.pinActions': 'Pripni dejanja',
      'table.unpinActions': 'Odpni dejanja',

      'drafts.confirmDiscard': 'Zavržem neshranjene spremembe?',
      'drafts.discard': 'Zavrzi',
      'drafts.keepEditing': 'Nadaljuj urejanje',
      'filters.assets.searchPlaceholder': 'Ime, delovni prostor ali lastnik',
      'filters.assets.ownership': 'Lastništvo',
      'filters.assets.ownershipMine': 'Moja',
      'filters.assets.ownershipShared': 'Deljena z mano',
      'filters.assets.shareableOnly': 'Samo tista, ki jih lahko delim',
      'filters.grants.searchPlaceholder': 'Vir ali lastnik',
      'filters.grants.namePlaceholder': 'Ime vira',
      'filters.grants.type': 'Vrsta',
      'filters.grants.grantedOnly': 'Samo tisti z dodelitvami',
      'filters.audit.searchPlaceholder': 'Kdo, kaj ali podrobnost',
      'filters.audit.actorPlaceholder': 'E-pošta izvajalca',
      'filters.audit.area': 'Področje',
      'filters.audit.areaGrants': 'Dostop',
      'filters.audit.areaUsers': 'Uporabniki',
      'filters.audit.areaTeams': 'Ekipe',
      'filters.audit.areaWorkspaces': 'Delovni prostori',
      'filters.audit.areaAssets': 'Sredstva',
      'filters.audit.when': 'Čas',
      'filters.audit.actor': 'Kdo',
      'filters.audit.newest': 'Najnovejše najprej',
      'filters.audit.oldest': 'Najstarejše najprej',
      'filters.users.searchPlaceholder': 'E-pošta',
      'filters.users.role': 'Vloga',
      'filters.users.deletedOnly': 'Samo izbrisani',
      'filters.users.email': 'E-pošta',
      'filters.users.content': 'Vsebina',
      'filters.workspaces.searchPlaceholder': 'Ime ali lastnik',
      'filters.workspaces.mineOnly': 'Samo moji',
      'filters.workspaces.contents': 'Vsebina',
      'filters.teams.searchPlaceholder': 'Ime ali član',
      'filters.teams.ownedOnly': 'Samo tiste v moji lasti',
      'filters.teams.unownedOnly': 'Samo brez lastnika',
      'filters.teams.members': 'Člani',
      'lobby.title': 'The Duke',
      'lobby.heading': 'Igraj The Duke',
      'lobby.description': 'Ustvarite novo igro in delite kodo, ali se pridružite igri, ki vam jo je nekdo poslal.',
      'lobby.createHeading': 'Začni igro',
      'lobby.timeControlLabel': 'Časovna omejitev',
      'lobby.timeControl.none': 'Brez',
      'lobby.timeControl.5': '5 minut',
      'lobby.timeControl.10': '10 minut',
      'lobby.timeControl.15+10': '15 minut + 10s/potezo',
      'lobby.createButton': 'Ustvari igro',
      'lobby.joinHeading': 'Pridruži se igri',
      'lobby.joinCodeLabel': 'Koda igre',
      'lobby.joinCodePlaceholder': 'ABC123',
      'lobby.joinButton': 'Pridruži se',
      'lobby.codeLabel': 'Koda igre',
      'lobby.waitingForOpponent': 'Čakanje na nasprotnika…',
      'lobby.ready': 'Oba igralca sta pripravljena — čas za postavitev plošče.',
      'lobby.hostLabel': 'Gostitelj',
      'lobby.opponentLabel': 'Nasprotnik',
      'lobby.leaveButton': 'Zapusti igro',
      'lobby.invalidCode': 'Vnesite 6-mestno kodo igre, ki vam jo je poslal nasprotnik.',
      'lobby.gameNotFound': 'Igre s to kodo ni bilo mogoče najti.',
      'lobby.gameFull': 'Ta igra že ima dva igralca.',
      'lobby.gameCreated': 'Igra ustvarjena.',
      'lobby.gameJoined': 'Pridružili ste se igri.',
      'lobby.goToGame': 'Postavi ploščo',

      'game.setup.title': 'Postavitev plošče',
      'game.setup.heading': 'Postavi svojo stran',
      'game.setup.description': 'Postavi svojega vojvodo, nato pa še dva pešca ob njem.',
      'game.codeLabel': 'Koda igre',
      'game.seat.host': 'Gostitelj',
      'game.seat.guest': 'Gost',
      'game.homeRow.host': 'Tvoja domača vrsta je označena spodaj.',
      'game.homeRow.guest': 'Tvoja domača vrsta je označena spodaj.',
      'game.tile.duke': 'Vojvoda',
      'game.tile.footman': 'Pešec',
      'game.tile.pikeman': 'Pikeman',
      'game.tile.bowman': 'Bowman',
      'game.tile.champion': 'Champion',
      'game.tile.general': 'General',
      'game.tile.knight': 'Knight',
      'game.tile.marshall': 'Marshall',
      'game.tile.priest': 'Priest',
      'game.tile.seer': 'Seer',
      'game.tile.wizard': 'Wizard',
      'game.tile.assassin': 'Assassin',
      'game.tile.dragoon': 'Dragoon',
      'game.tile.longbowman': 'Longbowman',
      'game.tile.ranger': 'Ranger',
      'game.setup.placeDuke': 'Postavi svojega vojvodo na eno od označenih polj.',
      'game.setup.placeFootmen': 'Postavi dva pešca ob svojega vojvodo.',
      'game.setup.sideReady': 'Tvoja stran je pripravljena.',
      'game.setup.submitButton': 'Potrdi postavitev',
      'game.setup.undoButton': 'Razveljavi',
      'game.setup.clearButton': 'Počisti',
      'game.setup.yourTurnToPlace': 'Postavi svoje figure spodaj.',
      'game.setup.waitingForOpponent': 'Čakanje na postavitev nasprotnika …',
      'game.setup.opponentReady': 'Nasprotnik je pripravljen.',
      'game.setup.bothReady': 'Obe strani sta pripravljeni — igra se začenja.',
      'game.setup.spectator': 'To igro le opazuješ — v njej nimaš svojega mesta.',
      'game.setup.squareAria': 'Vrstica {{row}}, stolpec {{col}}{{tile}}',
      'game.setup.illegalSquare': 'To polje ni veljavna postavitev.',
      'game.setup.rosterHeading': 'Tvoje ploščice',
      'game.loadFailed': 'Igre ni bilo mogoče naložiti. {{message}}',

      'game.play.title': 'Igra',
      'game.play.tip': 'Premakni ploščico na označeno polje, da izvedeš potezo.',
      'game.play.dismissTip': 'Razumem',
      'game.play.selectTile': 'Izberi eno od svojih ploščic za premik.',
      'game.play.selectTileOrDraw': 'Izberi eno od svojih ploščic za premik ali izvleci novo.',
      'game.play.selectTarget': 'Izberi označeno polje za premik nanj.',
      'game.play.opponentTurn': 'Čakanje na potezo nasprotnika…',
      'game.play.spectator': 'To igro spremljaš — v njej nimaš svojega mesta.',
      'game.play.youWon': 'Zajel si nasprotnikovega vojvodo — zmagal si!',
      'game.play.youLost': 'Tvoj vojvoda je bil zajet — izgubil si.',
      'game.play.cancelButton': 'Prekliči izbiro',
      'game.play.currentFace': 'Trenutna stran',
      'game.play.drawnTileName': 'Izvlečena ploščica: {{name}}',
      'game.play.nextFace': 'Po tej potezi',
      'game.play.otherSide': 'Druga stran',
      'game.play.selectCommandDestination': 'Izberi, kam poslati ukazano ploščico.',
      'game.play.selectDrawSquare': 'Postavi jo na prazno polje ob svojem vojvodi.',
      'game.play.drawButton': 'Izvleci ploščico',
      'game.play.bagCount': 'Vrečka: {{count}}',
      'game.play.inCheck': 'Tvoj vojvoda je v šahu.',
      'game.play.opponentInCheck': 'Nasprotnikov vojvoda je v šahu.',
      'game.play.checkmate': 'Šahmat — tvoj vojvoda nima izhoda.',
      'game.play.noLegalMoves': 'Nimaš veljavne poteze.',

      'games.title': 'Igre',
      'games.loading': 'Nalaganje…',
      'games.newGame': 'Nova igra',
      'games.empty': 'Še ni iger — začnite eno in zaigrajte.',
      'games.row.vs': 'proti {{opponent}}',
      'games.status.yourTurn': 'Vaša poteza',
      'games.status.opponentTurn': 'Poteza nasprotnika',
      'games.status.waiting': 'Čaka na nasprotnika',
      'games.status.finished': 'Končano',

      'books.title': 'Knjige',
      'books.empty': 'Ni še knjig.',
      'books.loading': 'Nalaganje knjig',
      'books.sharedBy': 'Deljeno od {{owner}}',
      'books.description': 'Demonstracijski seznam knjig, pripravljen na strežniku.',
      'books.loadFailed': 'Knjig ni bilo mogoče naložiti. {{message}}',

      'assets.added': 'Sredstvo je dodano',
      'assets.addFailed': 'Sredstva ni bilo mogoče dodati. {{message}}',
      'assets.title': 'Sredstva',
      'assets.empty': 'Ni še nobenega sredstva.',
      'assets.loading': 'Nalaganje sredstev',
      'assets.add': 'Dodaj',
      'assets.namePlaceholder': 'Ime sredstva',
      'assets.sharedBy': 'Deljeno od {{owner}}',
      'assets.personalWorkspace': 'Moj delovni prostor',
      'assets.description': 'Oprema, ki jo imate v lasti ali do nje dobili dostop.',
      'assets.nameLabel': 'Ime',
      'assets.workspaceLabel': 'Delovni prostor',
      'assets.inWorkspace': 'v {{name}}',
      'assets.emptyHint': 'Dodajte prvo, da začnete.',
      'assets.rowShare': 'Deli',
      'assets.rowRename': 'Preimenuj',
      'assets.renameTitle': 'Preimenuj {{name}}',
      'assets.rowDelete': 'Izbriši',
      'assets.saveRename': 'Shrani',
      'assets.cancel': 'Prekliči',
      'assets.renamed': 'Sredstvo preimenovano.',
      'assets.deleted': 'Sredstvo izbrisano.',
      'assets.confirmDelete': 'Izbrišem {{name}}? Vse dodelitve dostopa nanj gredo z njim.',
      'assets.confirmDeleteMany': 'Izbrišem {{count}} sredstev? Vse dodelitve dostopa nanje gredo z njimi.',
      'sharing.title': 'Kdo lahko dostopa',
      'sharing.paneTitle': 'Deljenje: {{name}}',
      'sharing.hint': 'Vsi na seznamu to vidijo. Branje je vedno vključeno.',
      'sharing.hintWorkspace': 'Vsi na seznamu lahko dostopajo do vsega v njem. Branje je vedno vključeno.',
      'sharing.loading': 'Nalaganje deljenja',
      'sharing.none': 'Še ni deljeno z nikomer.',
      'sharing.kindUser': 'Oseba',
      'sharing.kindTeam': 'Ekipa',
      'sharing.kindLink': 'E-poštna povezava',
      'sharing.linkSent': 'Povezava za dostop poslana.',
      'sharing.emailLabel': 'Njihova e-pošta',
      'sharing.emailPlaceholder': 'ime@example.com',
      'sharing.teamLabel': 'Ekipa',
      'sharing.noTeams': 'Niste še v nobeni ekipi — ustvarite jo na strani Ekipe.',
      'sharing.permRead': 'Ogled',
      'sharing.permUpdate': 'Urejanje',
      'sharing.permVerify': 'Potrjevanje opravljenega dela',
      'sharing.permDelete': 'Brisanje',
      'sharing.permManage': 'Deljenje naprej',
      'sharing.permAdd': 'Dodajanje',
      'sharing.expiryLabel': 'Poteče',
      'sharing.expiryNever': 'Nikoli',
      'sharing.expiryDays': '{{days}} dni',
      'sharing.expiresOn': 'Poteče {{date}}',
      'sharing.expiredOn': 'Poteklo {{date}}',
      'sharing.submit': 'Deli',
      'sharing.shared': 'Dostop dodeljen.',
      'sharing.revoke': 'Prekini deljenje',
      'sharing.revoked': 'Dostop odvzet.',
      'sharing.close': 'Končano',
      'sharing.targets.open': 'Ekipe in člani',
      'sharing.targets.heading': 'Povlecite nalogo sem, da jo delite',
      'sharing.targets.dropping': 'Spustite »{{title}}« na nekoga',
      'sharing.targets.showOffline': 'Prikaži nedejavne',
      'sharing.targets.empty': 'Niste še v nobeni ekipi — ustvarite jo na strani Ekipe.',
      'sharing.targets.emptyMembers': 'Ni članov za prikaz',
      'sharing.targets.owner': 'Lastnik',
      'sharing.targets.member': 'Član',
      'sharing.targets.online': 'Dejaven',
      'sharing.targets.offline': 'Nedejaven',
      'sharing.targets.sharedWith': 'Deljeno z: {{name}}.',
      'sharing.targets.alreadyShared': '{{name}} to že ima.',
      'sharing.targets.alreadyTaken': 'Že deljeno',
      'sharing.targets.dropzone': 'Spustite »{{title}}« sem za deljenje',
      'sharing.targets.dropzoneIdle': 'Zadržite za deljenje',
      'sharing.targets.externalHeading': 'Zunanji sodelavec',
      'sharing.targets.externalHint': 'Najprej povlecite delo sem, da zunanjemu naslovniku pošljete povezavo do njega.',
      'sharing.targets.externalEmailLabel': 'E-pošta',
      'sharing.targets.externalEmailPlaceholder': 'ime@primer.si',
      'sharing.targets.externalExpiryLabel': 'Povezava poteče čez',
      'sharing.targets.externalSend': 'Pošlji povezavo',
      'sharing.targets.externalSent': 'Povezava poslana na {{email}}.',
      'inbox.title': 'Deljeno z vami',
      'inbox.empty': 'Z vami še ni nič deljeno.',
      'inbox.headline': '{{grantedBy}} je z vami delil(a) »{{resourceLabel}}«',
      'inbox.notification': '{{grantedBy}} je z vami delil(a) »{{resourceLabel}}«.',
      'inbox.reminder.headline': '»{{jobTitle}}« kmalu zapade',
      'inbox.reminder.notification': 'Opomnik: »{{jobTitle}}« kmalu zapade.',
      'assets.move.title': 'Premakni',
      'assets.move.destinationLabel': 'V delovni prostor',
      'assets.move.submit': 'Premakni',
      'assets.move.moved': 'Sredstvo premaknjeno.',
      'assets.move.noTargets': 'Ni drugega delovnega prostora, kamor bi lahko dodajali.',
      'assets.showAddForm': 'Dodaj sredstvo',
      'assets.hideAddForm': 'Zapri',
      'assets.sharedHeading': 'Deljeno od',
      'assets.actionsHeading': 'Dejanja',
      'assets.detail.title': 'Sredstvo',
      'assets.detail.loading': 'Nalaganje sredstva',
      'assets.detail.notFoundTitle': 'Sredstvo ni najdeno',
      'assets.detail.notFoundDescription': 'Morda ne obstaja, ali pa nimate dostopa do njega.',
      'assets.detail.backToList': 'Nazaj na sredstva',
      'assets.detail.locationLabel': 'Lokacija',
      'assets.detail.locationNone': 'Brez lokacije',
      'assets.detail.locationSaved': 'Lokacija posodobljena',
      'assets.detail.tabsNavLabel': 'Razdelki sredstva',
      'assets.detail.tab.overview': 'Pregled',
      'assets.detail.tab.sharing': 'Deljenje',
      'assets.detail.tab.activity': 'Dejavnost',
      'jobs.detail.title': 'Delo',
      'jobs.detail.loading': 'Nalaganje dela',
      'jobs.detail.notFoundTitle': 'Delo ni najdeno',
      'jobs.detail.notFoundDescription': 'Morda ne obstaja, ali pa nimate dostopa do njega.',
      'jobs.detail.backToList': 'Nazaj na dela',
      'jobs.detail.backToShared': 'Nazaj na deljeno z mano',
      'jobs.detail.dueDateHeading': 'Rok',
      'jobs.detail.dueDateLabel': 'Rok',
      'jobs.detail.saveDueDate': 'Shrani',
      'jobs.detail.dueDateSaved': 'Rok shranjen.',
      'jobs.detail.instructionsHeading': 'Navodila',
      'jobs.detail.instructionsLabel': 'Navodila',
      'jobs.detail.instructionsPlaceholder': 'Kako opraviti to delo ...',
      'jobs.detail.saveInstructions': 'Shrani navodila',
      'jobs.detail.instructionsSaved': 'Navodila shranjena.',
      'jobs.detail.photoHeading': 'Dokazilo o opravljenem delu',
      'jobs.detail.uploadPhoto': 'Naloži fotografijo',
      'jobs.detail.replacePhoto': 'Zamenjaj fotografijo',
      'jobs.detail.removePhoto': 'Odstrani fotografijo',
      'jobs.detail.photoSaved': 'Fotografija naložena.',
      'jobs.detail.photoRemoved': 'Fotografija odstranjena.',
      'jobs.detail.instructionPhotosHeading': 'Referenčne slike',
      'jobs.detail.addInstructionPhoto': 'Dodaj sliko',
      'jobs.detail.removeInstructionPhoto': 'Odstrani sliko',
      'jobs.detail.instructionPhotoAdded': 'Slika dodana.',
      'jobs.detail.instructionPhotoRemoved': 'Slika odstranjena.',
      'jobs.detail.instructionPhotoLimitReached': 'Doseženo je omejeno število — najprej odstrani eno sliko.',
      'jobs.detail.procedureHeading': 'Procedura',
      'jobs.detail.procedureFieldLabel': 'Sledi proceduri',
      'jobs.detail.procedureNone': 'Brez',
      'jobs.detail.procedureChanged': 'Procedura posodobljena.',
      'jobs.detail.procedureWillFreeze': 'Kopija te procedure se zamrzne na to nalogo v trenutku, ko se začne.',
      'jobs.detail.procedureFrozenAt':
        'Sledi kopiji, zamrznjeni pri različici {{revision}} — spremembe procedure od takrat tukaj ne veljajo.',
      'jobs.detail.steps.heading': 'Koraki',
      'jobs.detail.steps.tick': 'Označi',
      'jobs.detail.steps.untick': 'Odznači',
      'jobs.detail.steps.tickedBy': 'Označil {{email}}',
      'jobs.detail.steps.stepTicked': 'Korak označen.',
      'jobs.detail.steps.stepUnticked': 'Korak odznačen.',
      'jobs.detail.steps.addEvidence': 'Dodaj fotografijo',
      'jobs.detail.steps.removeEvidence': 'Odstrani fotografijo',
      'jobs.detail.steps.evidenceAdded': 'Fotografija dodana.',
      'jobs.detail.steps.evidenceRemoved': 'Fotografija odstranjena.',
      'jobs.detail.steps.evidenceLimitReached': 'Doseženo je največje število — odstranite eno za dodajanje nove.',
      'jobs.detail.recurrence.heading': 'Ponavljanje',
      'jobs.detail.recurrence.start': 'Naj se ta naloga ponavlja',
      'jobs.detail.recurrence.stop': 'Ustavi ponavljanje',
      'jobs.detail.recurrence.cleared': 'Ponavljanje odstranjeno.',
      'jobs.detail.recurrence.saved': 'Ponavljanje shranjeno.',
      'jobs.detail.recurrence.modeLabel': 'Sprožilec',
      'jobs.detail.recurrence.modeAfterCompletion': 'Po zaključku',
      'jobs.detail.recurrence.modeFixedCalendar': 'Fiksni koledar',
      'jobs.detail.recurrence.intervalLabel': 'Vsakih (dni)',
      'jobs.detail.recurrence.anchorLabel': 'Izhodiščni datum',
      'jobs.detail.recurrence.save': 'Shrani',
      'jobs.detail.completionNoteHeading': 'Opomba ob zaključku',
      'jobs.detail.completionNoteLabel': 'Ugotovljene težave ali opombe za naslednjič',
      'jobs.detail.completionNotePlaceholder': 'Kar naj ve naslednja oseba ...',
      'jobs.detail.saveCompletionNote': 'Shrani opombo',
      'jobs.detail.completionNoteSaved': 'Opomba ob zaključku shranjena.',
      'jobs.detail.review.heading': 'Potrditev',
      'jobs.detail.review.verifiedBy': 'Potrdil/a {{email}}, {{date}}',
      'jobs.detail.review.verificationNoteLabel': 'Ugotovitve pregledovalca',
      'jobs.detail.review.verificationNotePlaceholder': 'Kaj ste preverili in kaj ste ugotovili?',
      'jobs.detail.review.verify': 'Potrdi',
      'jobs.detail.review.sendBack': 'Vrni v dodelavo',
      'assets.detail.activity.loading': 'Nalaganje dejavnosti',
      'assets.detail.activity.empty': 'Še ni zabeležene dejavnosti.',

      'nav.calendar': 'Koledar',
      'calendar.title': 'Koledar',
      'calendar.description': 'Vse naloge z rokom, prikazane po mesecih.',
      'calendar.prevMonth': 'Prejšnji mesec',
      'calendar.nextMonth': 'Naslednji mesec',
      'calendar.today': 'Danes',

      'admin.audit.title': 'Sled dejanj',
      'admin.audit.description':
        'Kdo je spremenil dostop in kaj je spremenil. Zapisi se samo dodajajo — nikoli se ne urejajo ali brišejo.',
      'admin.audit.window': 'Prikazanih je zadnjih {{limit}} zapisov.',
      'admin.audit.loading': 'Nalaganje sledi dejanj',
      'admin.audit.empty': 'Ni še zapisov.',
      'admin.audit.emptyHint': 'Zapisi se pojavijo takoj, ko kdo spremeni dostop.',
      'admin.audit.whenHeading': 'Kdaj',
      'admin.audit.actorHeading': 'Kdo',
      'admin.audit.actionHeading': 'Kaj je storil',
      'admin.audit.targetHeading': 'Nad čim',
      'admin.audit.detailHeading': 'Podrobnost',
      'admin.audit.actionsHeading': 'Dejanja',
      'admin.audit.action.grant.created': 'Dodelil dostop',
      'admin.audit.action.grant.replaced': 'Spremenil dostop',
      'admin.audit.action.grant.revoked': 'Odvzel dostop',
      'admin.audit.action.user.created': 'Ustvaril račun',
      'admin.audit.action.user.role-changed': 'Spremenil vlogo',
      'admin.audit.action.user.deleted': 'Izbrisal račun',
      'admin.audit.action.user.restored': 'Obnovil račun',
      'admin.audit.action.user.password-reset': 'Ponastavil geslo',
      'admin.audit.action.team.created': 'Ustvaril ekipo',
      'admin.audit.action.team.renamed': 'Preimenoval ekipo',
      'admin.audit.action.team.deleted': 'Izbrisal ekipo',
      'admin.audit.action.team.member-added': 'Dodal člana',
      'admin.audit.action.team.member-removed': 'Odstranil člana',
      'admin.audit.action.workspace.created': 'Ustvaril delovni prostor',
      'admin.audit.action.workspace.renamed': 'Preimenoval delovni prostor',
      'admin.audit.action.workspace.deleted': 'Izbrisal delovni prostor',
      'admin.audit.action.workspace.invite-created': 'Povabil v delovni prostor',
      'admin.audit.action.workspace.invite-revoked': 'Preklical povabilo v delovni prostor',
      'admin.audit.action.workspace.member-joined': 'Pridružil se delovnemu prostoru',
      'admin.audit.action.workspace.member-role-changed': 'Spremenil vlogo člana',
      'admin.audit.action.workspace.member-removed': 'Odstranil člana delovnega prostora',
      'admin.audit.action.asset.created': 'Ustvaril sredstvo',
      'admin.audit.action.asset.renamed': 'Preimenoval sredstvo',
      'admin.audit.action.asset.deleted': 'Izbrisal sredstvo',
      'admin.audit.action.asset.moved': 'Premaknil sredstvo',
      'admin.audit.action.job.created': 'Ustvaril nalogo',
      'admin.audit.action.job.renamed': 'Preimenoval nalogo',
      'admin.audit.action.job.status-changed': 'Spremenil stanje naloge',
      'admin.audit.action.job.deleted': 'Izbrisal nalogo',
      'admin.audit.action.job.moved': 'Premaknil nalogo',
      'admin.audit.kind.user': 'Uporabnik',
      'admin.audit.kind.team': 'Ekipa',
      'admin.audit.kind.workspace': 'Delovni prostor',
      'admin.audit.kind.asset': 'Sredstvo',
      'admin.audit.kind.book': 'Knjiga',
      'admin.audit.kind.job': 'Naloga',
      'admin.audit.kind.grant': 'Dodelitev',
      'admin.audit.kind.procedure': 'Procedura',
      'admin.audit.kind.location': 'Lokacija',
      'admin.audit.kind.taskType': 'Vrsta naloge',
      'admin.errors.title': 'Iskanje napak',
      'admin.errors.description': 'Napake, ki jih je zabeležil strežnik, s podrobnostmi, ki jih klicatelj ne vidi.',
      'admin.errors.lookupTitle': 'Iskanje po referenci',
      'admin.errors.lookupDescription': 'Referenca, ki jo je uporabnik videl ob napaki.',
      'admin.errors.requestIdLabel': 'Referenca',
      'admin.errors.requestIdPlaceholder': 'ID zahteve',
      'admin.errors.search': 'Poišči',
      'admin.errors.recentTitle': 'Nedavne napake',
      'admin.errors.recentEmpty': 'Ni zabeleženih napak.',
      'admin.errors.reload': 'Osveži',

      'admin.users.title': 'Uporabniki',
      'admin.users.description': 'Računi, njihove vloge in podatki, ki jih je izbrisan račun pustil za sabo.',
      'admin.users.listTitle': 'Vsi računi',
      'admin.users.userHeading': 'Uporabnik',
      'admin.users.statusHeading': 'Stanje',
      'admin.users.contentHeading': 'Vsebina',
      'admin.users.contentCount': '{{assets}} sredstev, {{books}} knjig',
      'admin.users.actionsHeading': 'Dejanja',
      'admin.users.passwordLabel': 'Novo geslo',
      'admin.users.showCreateForm': 'Dodaj uporabnika',
      'admin.users.hideCreateForm': 'Zapri',
      'admin.users.newUserEmailLabel': 'E-poštni naslov',
      'admin.users.newUserEmailPlaceholder': 'oseba@example.com',
      'admin.users.newUserPasswordLabel': 'Geslo',
      'admin.users.newUserPasswordPlaceholder': 'Začetno geslo',
      'admin.users.create': 'Ustvari uporabnika',
      'admin.users.created': 'Uporabnik ustvarjen.',
      'admin.users.loading': 'Nalaganje uporabnikov',
      'admin.users.empty': 'Ni uporabnikov.',
      'admin.users.reload': 'Osveži',
      'admin.users.roleAdmin': 'Skrbnik',
      'admin.users.roleUser': 'Uporabnik',
      'admin.users.makeAdmin': 'Nastavi za skrbnika',
      'admin.users.makeUser': 'Nastavi za uporabnika',
      'admin.users.delete': 'Izbriši',
      'admin.users.restore': 'Obnovi',
      'admin.users.resetPassword': 'Ponastavi geslo',
      'admin.users.resetPasswordTitle': 'Ponastavi geslo za {{email}}',
      'admin.users.newPasswordPlaceholder': 'Novo geslo',
      'admin.users.save': 'Shrani',
      'admin.users.cancel': 'Prekliči',
      'admin.users.you': 'Vi',
      'admin.users.deletedAt': 'Izbrisan {{date}}',
      'admin.users.orphaned': 'Osiroteli podatki: {{assets}} sredstev, {{books}} knjig',
      'admin.users.confirmDelete': 'Izbrišem {{email}}? Podatki ostanejo in račun je mogoče obnoviti.',
      'admin.users.confirmDeleteMany': 'Izbrišem {{count}} računov? Podatki ostanejo in vsak račun je mogoče obnoviti.',
      'admin.users.roleChanged': 'Vloga posodobljena.',
      'admin.users.deleted': 'Uporabnik izbrisan — podatki so ohranjeni.',
      'admin.users.restored': 'Uporabnik obnovljen.',
      'admin.users.passwordReset': 'Geslo ponastavljeno.',

      'teams.description': 'Skupine ljudi, s katerimi lahko delite. Ko ekipo ustvarite, postanete njen član.',
      'teams.listTitle': 'Ekipe v vaši lasti ali članstvu',
      'teams.nameLabel': 'Ime',
      'teams.ownerHeading': 'Lastnik',
      'teams.membersHeading': 'Člani',
      'teams.actionsHeading': 'Dejanja',
      'teams.emailLabel': 'E-pošta člana',
      'teams.createHint': 'Postanete lastnik in prvi član.',
      'teams.ownerBadge': 'Lastnik',
      'teams.memberCount': '{{count}} članov',
      'teams.emptyHint': 'Ustvarite ekipo, da začnete deliti.',
      'teams.showCreateForm': 'Nova ekipa',
      'teams.hideCreateForm': 'Zapri',
      'teams.title': 'Moje ekipe',
      'teams.scopeMine': 'Moje ekipe',
      'teams.scopeAdmin': 'Vse ekipe',
      'teams.loading': 'Nalaganje ekip',
      'teams.empty': 'Še niste v nobeni ekipi.',
      'teams.namePlaceholder': 'Ime ekipe',
      'teams.create': 'Ustvari ekipo',
      'teams.ownedByMe': 'Ekipa je vaša',
      'teams.ownedBy': 'Lastnik: {{owner}}',
      'teams.noOwner': 'skrbnik',
      'teams.addMember': 'Dodaj člana',
      'teams.addMemberTitle': 'Dodaj člana v {{name}}',
      'teams.emailPlaceholder': 'E-pošta uporabnika',
      'teams.saveMember': 'Dodaj',
      'teams.cancel': 'Prekliči',
      'teams.remove': 'Odstrani',
      'teams.leave': 'Zapusti',
      'teams.makeOwner': 'Nastavi za lastnika',
      'teams.ownerChanged': 'Lastništvo prenešeno.',
      'teams.confirmMakeOwner': 'Predam {{name}} uporabniku {{email}}? Odtlej jo bo lahko upravljal samo on.',
      'teams.delete': 'Izbriši',
      'teams.rename': 'Preimenuj',
      'teams.renameTitle': 'Preimenuj {{name}}',
      'teams.saveRename': 'Shrani',
      'teams.noMembers': 'Ni članov.',
      'teams.created': 'Ekipa ustvarjena.',
      'teams.deleted': 'Ekipa izbrisana.',
      'teams.renamed': 'Ekipa preimenovana.',
      'teams.memberAdded': 'Član dodan.',
      'teams.memberRemoved': 'Član odstranjen.',
      'teams.left': 'Zapustili ste ekipo.',
      'teams.confirmDelete': 'Izbrišem ekipo {{name}}? Njene dodelitve dostopa bodo odvzete.',
      'teams.confirmDeleteMany': 'Izbrišem {{count}} ekip? Njihove dodelitve dostopa bodo odvzete.',

      'settings.title': 'Nastavitve',
      'settings.description': 'Kako in kdaj želite biti opomnjeni na dela, ki zapadejo.',
      'settings.accountTitle': 'Račun',
      'settings.displayNameLabel': 'Prikazno ime',
      'settings.displayNamePlaceholder': 'Ime, ki ga drugi vidijo namesto vašega e-naslova',
      'settings.saveNameLabel': 'Shrani ime',
      'settings.nameSaved': 'Prikazno ime shranjeno.',
      'settings.currentPasswordLabel': 'Trenutno geslo',
      'settings.newPasswordLabel': 'Novo geslo',
      'settings.changePasswordLabel': 'Spremeni geslo',
      'settings.passwordChanged': 'Geslo spremenjeno.',
      'settings.enabledLabel': 'Pošlji mi opomnike',
      'settings.leadDaysLabel': 'Opomni me',
      'settings.leadDays.dayOf': 'Na dan zapadlosti',
      'settings.leadDays.days': '{{count}} dni pred zapadlostjo',
      'settings.jobSetLabel': 'Katera dela',
      'settings.jobSet.assigned': 'Samo dela, dodeljena ali prevzeta zame',
      'settings.jobSet.involved': 'Dela, dodeljena meni, ali v moji lastni zbirki',
      'settings.jobSet.visible': 'Vsa dela, ki jih vidim',
      'settings.hourOfDayLabel': 'Ura dneva (0-23)',
      'settings.timeZoneLabel': 'Časovni pas',
      'settings.emailLabel': 'Pošlji po e-pošti',
      'settings.inAppLabel': 'Prikaži v mojem predalu',
      'settings.saveLabel': 'Shrani',
      'settings.saved': 'Nastavitve shranjene.',
      'settings.export.title': 'Izvoz podatkov',
      'settings.export.description': 'Prenesite kopijo svojih del.',
      'settings.export.jsonLabel': 'Prenesi JSON',
      'settings.export.csvLabel': 'Prenesi CSV',
      'settings.export.downloaded': 'Izvoz prenesen.',
      'settings.calendarFeed.title': 'Koledarski vir',
      'settings.calendarFeed.description':
        'Naročite se v telefonskem ali namiznem koledarju, da vidite dela, ki zapadejo, brez odpiranja aplikacije.',
      'settings.calendarFeed.copyLabel': 'Kopiraj povezavo',
      'settings.calendarFeed.copied': 'Povezava kopirana.',
      'settings.calendarFeed.mintLabel': 'Vklopi',
      'settings.calendarFeed.regenerateLabel': 'Ustvari novo povezavo',
      'settings.calendarFeed.revokeLabel': 'Izklopi',
      'settings.calendarFeed.minted': 'Koledarski vir pripravljen.',
      'settings.calendarFeed.revoked': 'Koledarski vir izklopljen.',
      'settings.push.title': 'Potisna obvestila',
      'settings.push.description': 'Prejmite obvestilo na tej napravi, ko delo zapade, tudi ko je aplikacija zaprta.',
      'settings.push.toggleLabel': 'Obvesti me na tej napravi',
      'settings.push.enableLabel': 'Vklopi',
      'settings.push.disableLabel': 'Izklopi',
      'settings.push.enabled': 'Potisna obvestila vklopljena za to napravo.',
      'settings.push.disabled': 'Potisna obvestila izklopljena za to napravo.',
      'settings.push.unsupported': 'Ta brskalnik ne podpira potisnih obvestil.',
      'settings.push.blocked': 'Obvestila so blokirana v nastavitvah brskalnika — najprej jih dovolite tam.',
      'settings.push.serverUnavailable': 'Potisna obvestila na strežniku niso nastavljena.',
      'settings.push.failed': 'Potisnih obvestil ni bilo mogoče spremeniti. Poskusite znova.',
      'workspaces.description': 'Vsebniki, v katerih so vaši viri, in kdo drug lahko do njih dostopa.',
      'workspaces.listTitle': 'Vaši delovni prostori',
      'workspaces.listDescription': 'Sredstvo premaknete med njimi kar iz njegove vrstice na strani Sredstva.',
      'workspaces.nameLabel': 'Ime',
      'workspaces.nameHeading': 'Delovni prostor',
      'workspaces.ownerHeading': 'Lastnik',
      'workspaces.contentsHeading': 'Vsebina',
      'workspaces.accessHeading': 'Vaš dostop',
      'workspaces.actionsHeading': 'Dejanja',
      'workspaces.emptyHint': 'Ustvarite enega za razvrstitev svojih virov.',
      'workspaces.showCreateForm': 'Nov delovni prostor',
      'workspaces.hideCreateForm': 'Zapri',
      'workspaces.title': 'Delovni prostori',
      'workspaces.loading': 'Nalaganje delovnih prostorov',
      'workspaces.empty': 'Ni delovnih prostorov.',
      'workspaces.assetCount': '{{count}} sredstev',
      'workspaces.personalBadge': 'Osebni',
      'workspaces.perm.read': 'Branje',
      'workspaces.perm.add': 'Dodajanje',
      'workspaces.perm.update': 'Urejanje',
      'workspaces.perm.delete': 'Brisanje',
      'workspaces.perm.manage': 'Upravljanje',
      'workspaces.namePlaceholder': 'Ime delovnega prostora',
      'workspaces.create': 'Ustvari',
      'workspaces.rename': 'Preimenuj',
      'workspaces.renameTitle': 'Preimenuj {{name}}',
      'workspaces.share': 'Deli',
      'workspaces.members': 'Člani',
      'workspaces.members.paneTitle': 'Člani prostora {{name}}',
      'workspaces.members.title': 'Člani',
      'workspaces.members.memberHeading': 'Član',
      'workspaces.members.roleHeading': 'Vloga',
      'workspaces.members.addedHeading': 'Pridružen',
      'workspaces.members.actionsHeading': 'Dejanja',
      'workspaces.members.remove': 'Odstrani',
      'workspaces.members.confirmRemove': 'Odstranim {{email}} iz tega delovnega prostora?',
      'workspaces.members.removed': 'Član odstranjen.',
      'workspaces.members.roleChanged': 'Vloga posodobljena.',
      'workspaces.members.inviteEmailLabel': 'Povabi po e-pošti',
      'workspaces.members.inviteRoleLabel': 'Kot',
      'workspaces.members.inviteSubmit': 'Pošlji povabilo',
      'workspaces.members.inviteSent': 'Povabilo poslano.',
      'workspaces.members.inviteRevoked': 'Povabilo preklicano.',
      'workspaces.members.pendingTitle': 'Čakajoča povabila',
      'workspaces.members.pending': 'čaka',
      'workspaces.members.revoked': 'preklicano',
      'workspaces.members.expired': 'poteklo',
      'workspaces.members.revoke': 'Prekliči',
      'workspaces.invite.title': 'Povabilo v delovni prostor',
      'workspaces.invite.accepting': 'Sprejemanje povabila…',
      'workspaces.invite.joined': 'Pridružili ste se prostoru {{workspace}}.',
      'workspaces.invite.alreadyMember': 'Ste že član prostora {{workspace}}.',
      'workspaces.invite.invalid': 'To povabilo ni več veljavno',
      'workspaces.invite.invalidHelp':
        'Povezava je morda potekla, bila preklicana ali že uporabljena. Prosite za novo.',
      'workspaces.invite.goHome': 'Odpri aplikacijo',
      'workspaces.saveRename': 'Shrani',
      'workspaces.delete': 'Izbriši',
      'workspaces.cancel': 'Prekliči',
      'workspaces.created': 'Delovni prostor ustvarjen.',
      'workspaces.renamed': 'Delovni prostor preimenovan.',
      'workspaces.deleted': 'Delovni prostor izbrisan.',
      'workspaces.confirmDelete': 'Izbrišem delovni prostor {{name}}? Njegove dodelitve dostopa bodo odvzete.',
      'workspaces.confirmDeleteMany': 'Izbrišem {{count}} delovnih prostorov? Njihove dodelitve dostopa bodo odvzete.',

      'nav.jobs': 'Naloge',
      'jobs.description': 'Delo, ki ga je treba opraviti, sledeno od "za opraviti" do "končano".',
      'jobs.listTitle': 'Vaše naloge',
      'jobs.listDescription': 'Nalogi dodelite sredstvo, da poveste, česa se tiče.',
      'jobs.titleLabel': 'Naslov',
      'jobs.titlePlaceholder': 'Naslov naloge',
      'jobs.workspaceFieldLabel': 'Delovni prostor',
      'jobs.assetFieldLabel': 'Sredstvo (neobvezno)',
      'jobs.dueOnFieldLabel': 'Rok (neobvezno)',
      'jobs.locationFieldLabel': 'Lokacija',
      'jobs.locationDerive': 'Od sredstva',
      'jobs.locationNone': 'Brez lokacije',
      'jobs.detail.locationLabel': 'Lokacija',
      'jobs.taskTypeFieldLabel': 'Vrsta naloge',
      'jobs.taskTypeNone': 'Brez vrste',
      'jobs.detail.taskTypeLabel': 'Vrsta naloge',
      'jobs.titleHeading': 'Naloga',
      'jobs.statusHeading': 'Stanje',
      'jobs.ownerHeading': 'Lastnik',
      'jobs.workspaceHeading': 'Delovni prostor',
      'jobs.taskTypeHeading': 'Vrsta naloge',
      'jobs.locationHeading': 'Lokacija',
      'jobs.assetHeading': 'Sredstvo',
      'jobs.dueOnHeading': 'Rok',
      'jobs.accessHeading': 'Vaš dostop',
      'jobs.actionsHeading': 'Dejanja',
      'jobs.emptyHint': 'Ustvarite eno, da začnete slediti delu.',
      'jobs.showCreateForm': 'Nova naloga',
      'jobs.hideCreateForm': 'Zapri',
      'jobs.title': 'Naloge',
      'jobs.loading': 'Nalaganje nalog',
      'jobs.empty': 'Ni nalog.',
      'jobs.status.todo': 'Za opraviti',
      'jobs.status.doing': 'V teku',
      'jobs.status.done': 'Končano',
      'jobs.status.verified': 'Potrjeno',
      'jobs.start': 'Začni',
      'jobs.complete': 'Zaključi',
      'jobs.reopen': 'Ponovno odpri',
      'jobs.verify': 'Potrdi',
      'jobs.sendBack': 'Vrni v dodelavo',
      'jobs.verified': 'Naloga potrjena.',
      'jobs.sentBack': 'Naloga vrnjena v dodelavo.',
      'jobs.confirmSendBack': 'Vrnem nalogo {{name}} v dodelavo? To izbriše zaključek in prevzem.',
      'jobs.claim': 'Prevzemi',
      'jobs.release': 'Sprosti',
      'jobs.claimedByYou': 'Prevzeto s strani vas',
      'jobs.claimedByOther': 'Prevzel/a {{email}}',
      'jobs.assignedToYouBy': 'Dodelil/a vam je {{by}}',
      'jobs.assignedToOtherBy': 'Dodelil/a osebi {{email}}: {{by}}',
      'jobs.claimed': 'Naloga prevzeta.',
      'jobs.claimReleased': 'Prevzem sproščen.',
      'jobs.assign': 'Dodeli',
      'jobs.assigneeLabel': 'E-pošta prejemnika',
      'jobs.assigneePlaceholder': 'ime@example.com',
      'jobs.assigned': 'Naloga je bila dodeljena osebi {{email}}.',
      'jobs.overdueBadge': 'Zapoznelo',
      'jobs.perm.read': 'Branje',
      'jobs.perm.add': 'Dodajanje',
      'jobs.perm.update': 'Urejanje',
      'jobs.perm.verify': 'Potrjevanje',
      'jobs.perm.delete': 'Brisanje',
      'jobs.perm.manage': 'Upravljanje',
      'jobs.create': 'Ustvari',
      'jobs.rename': 'Preimenuj',
      'jobs.renameTitle': 'Preimenuj {{name}}',
      'jobs.share': 'Deli',
      'jobs.saveRename': 'Shrani',
      'jobs.delete': 'Izbriši',
      'jobs.cancel': 'Prekliči',
      'jobs.created': 'Naloga ustvarjena.',
      'jobs.renamed': 'Naloga preimenovana.',
      'jobs.deleted': 'Naloga izbrisana.',
      'jobs.statusChanged': 'Stanje posodobljeno.',
      'jobs.confirmDelete': 'Izbrišem nalogo {{name}}? Njene dodelitve dostopa bodo odvzete.',
      'jobs.confirmDeleteMany': 'Izbrišem {{count}} nalog? Njihove dodelitve dostopa bodo odvzete.',
      'jobs.recurringBadge': 'Ponavljajoče',
      'jobs.repeatsBadge': 'Se ponavlja',
      'filters.jobs.searchPlaceholder': 'Iskanje nalog…',
      'filters.jobs.status': 'Stanje',
      'filters.jobs.overdueOnly': 'Samo zapoznele',
      'filters.jobs.dueOn': 'Rok',
      'filters.jobs.taskType': 'Vrsta naloge',
      'filters.jobs.location': 'Lokacija',
      'filters.audit.areaJobs': 'Naloge',
      'admin.access.typeJob': 'Naloga',
      'admin.access.typeProcedure': 'Procedura',

      'nav.procedures': 'Procedure',
      'nav.procedure': 'Procedura',
      'nav.locations': 'Lokacije',
      'nav.location': 'Lokacija',
      'locations.title': 'Lokacije',
      'locations.description':
        'Kje se opravlja delo — Podjetje → Tovarna → Hala → Linija ali kamp → recepcija → sanitarije. Gnezdite poljubno globoko.',
      'locations.nameLabel': 'Ime',
      'locations.namePlaceholder': 'Stavba, soba, boks…',
      'locations.workspaceFieldLabel': 'Delovni prostor',
      'locations.parentFieldLabel': 'Znotraj',
      'locations.parentNone': 'Najvišja raven',
      'locations.showCreateForm': 'Nova lokacija',
      'locations.hideCreateForm': 'Zapri',
      'locations.loading': 'Nalaganje lokacij',
      'locations.empty': 'Še ni lokacij.',
      'locations.emptyHint': 'Dodajte eno, da poveste, kje se opravlja delo.',
      'locations.expand': 'Razširi',
      'locations.collapse': 'Strni',
      'locations.create': 'Ustvari',
      'locations.created': 'Lokacija ustvarjena.',
      'locations.confirmDelete':
        'Izbrišem {{name}}? Njene podlokacije se premaknejo k nadrejeni, sredstva na tej lokaciji pa izgubijo lokacijo.',
      'locations.detail.title': 'Lokacija',
      'locations.detail.backToList': 'Nazaj na lokacije',
      'locations.detail.loading': 'Nalaganje lokacije',
      'locations.detail.notFoundTitle': 'Lokacija ni najdena',
      'locations.detail.notFoundDescription': 'Morda ne obstaja ali pa nimate dostopa.',
      'locations.detail.workspaceLabel': 'Delovni prostor',
      'locations.detail.nameFieldLabel': 'Ime',
      'locations.detail.saveName': 'Shrani ime',
      'locations.detail.nameSaved': 'Ime shranjeno.',
      'locations.detail.parentFieldLabel': 'Znotraj',
      'locations.detail.parentSaved': 'Premaknjeno.',
      'locations.detail.childrenHeading': 'Lokacije znotraj te',
      'locations.detail.noChildren': 'Tu še ni ničesar gnezdenega.',
      'locations.detail.assetsHereHeading': 'Sredstva tukaj',
      'locations.detail.noAssetsHere': 'Na tej lokaciji ni sredstev.',
      'locations.detail.jobsHereHeading': 'Naloge tukaj',
      'locations.detail.noJobsHere': 'Na tej lokaciji ni zabeleženih nalog.',
      'locations.detail.delete': 'Izbriši lokacijo',
      'nav.shared': 'Deljeno z mano',
      'shared.title': 'Deljeno z mano',
      'shared.description': 'Dela, do katerih so vam drugi dodelili dostop.',
      'shared.listTitle': 'Dela, deljena z vami',
      'shared.loading': 'Nalaganje deljenih del',
      'shared.empty': 'Z vami še ni ničesar deljenega.',
      'shared.emptyHint': 'Ko nekdo z vami deli delo, se prikaže tukaj.',
      'shared.sharedBy': 'Deljeno od {{owner}}',
      'nav.taskTypes': 'Vrste nalog',
      'taskTypes.title': 'Vrste nalog',
      'taskTypes.description':
        'Kakšne vrste je delo — čiščenje, pregled, popravilo, dostava. Uporabite jih za razvrščanje in filtriranje seznama nalog.',
      'taskTypes.nameLabel': 'Ime',
      'taskTypes.namePlaceholder': 'Čiščenje, pregled…',
      'taskTypes.workspaceFieldLabel': 'Delovni prostor',
      'taskTypes.showCreateForm': 'Nova vrsta naloge',
      'taskTypes.hideCreateForm': 'Zapri',
      'taskTypes.empty': 'Še ni vrst nalog.',
      'taskTypes.emptyHint': 'Dodajte eno, da razvrstite vrste dela, ki jih opravljate.',
      'taskTypes.create': 'Ustvari',
      'taskTypes.created': 'Vrsta naloge ustvarjena.',
      'taskTypes.rename': 'Preimenuj',
      'taskTypes.renamed': 'Vrsta naloge preimenovana.',
      'taskTypes.saveName': 'Shrani',
      'taskTypes.delete': 'Izbriši',
      'taskTypes.deleted': 'Vrsta naloge izbrisana.',
      'taskTypes.confirmDelete': 'Izbrišem {{name}}? Naloge te vrste ostanejo, izgubijo le oznako.',
      'taskTypes.nameHeading': 'Ime',
      'taskTypes.workspaceHeading': 'Delovni prostor',
      'taskTypes.accessHeading': 'Dostop',
      'taskTypes.actionsHeading': 'Dejanja',
      'taskTypes.perm.read': 'Branje',
      'taskTypes.perm.add': 'Dodajanje',
      'taskTypes.perm.update': 'Urejanje',
      'taskTypes.perm.verify': 'Potrjevanje',
      'taskTypes.perm.delete': 'Brisanje',
      'taskTypes.perm.manage': 'Upravljanje',
      'procedures.title': 'Procedure',
      'procedures.description':
        'Ponovno uporabna navodila, na katera se sklicujejo naloge in pravila — uredite na enem mestu, vsaka naloga, ki se začne po vaši spremembi, jo vidi.',
      'procedures.listTitle': 'Vaše procedure',
      'procedures.listDescription':
        'Naloga zamrzne svojo kopijo v trenutku, ko se začne, zato poznejše spremembe ne vplivajo na delo, ki že poteka.',
      'procedures.titleLabel': 'Naslov',
      'procedures.titlePlaceholder': 'Kaj ta procedura zajema',
      'procedures.workspaceFieldLabel': 'Delovni prostor',
      'procedures.bodyLabel': 'Navodila (neobvezno)',
      'procedures.bodyPlaceholder': 'Korak za korakom, ali kar koli naslednja oseba potrebuje',
      'procedures.titleHeading': 'Procedura',
      'procedures.bodyHeading': 'Navodila',
      'procedures.revisionHeading': 'Različica',
      'procedures.revisionLabel': 'Razl. {{count}}',
      'procedures.workspaceHeading': 'Delovni prostor',
      'procedures.accessHeading': 'Dostop',
      'procedures.actionsHeading': 'Dejanja',
      'procedures.showCreateForm': 'Nova procedura',
      'procedures.hideCreateForm': 'Zapri',
      'procedures.loading': 'Nalaganje procedur',
      'procedures.empty': 'Ni procedur.',
      'procedures.emptyHint': 'Ustvarite eno, da vam ni treba ponovno vpisovati istih navodil.',
      'procedures.create': 'Ustvari',
      'procedures.created': 'Procedura ustvarjena.',
      'procedures.deleted': 'Procedura izbrisana.',
      'procedures.delete': 'Izbriši',
      'procedures.confirmDelete':
        'Izbrišem proceduro {{name}}? Naloge, ki ji že sledijo, obdržijo svojo zamrznjeno kopijo; naloge in pravila, ki se nanjo le sklicujejo, izgubijo povezavo.',
      'procedures.perm.read': 'Branje',
      'procedures.perm.add': 'Dodajanje',
      'procedures.perm.update': 'Urejanje',
      'procedures.perm.delete': 'Brisanje',
      'procedures.perm.manage': 'Upravljanje',

      'procedures.detail.title': 'Procedura',
      'procedures.detail.backToList': 'Nazaj na procedure',
      'procedures.detail.loading': 'Nalaganje procedure',
      'procedures.detail.notFoundTitle': 'Procedura ni najdena',
      'procedures.detail.notFoundDescription': 'Morda ne obstaja, ali pa nimate dostopa do nje.',
      'procedures.detail.titleFieldLabel': 'Naslov',
      'procedures.detail.bodyFieldLabel': 'Uvod (neobvezno)',
      'procedures.detail.save': 'Shrani',
      'procedures.detail.saved': 'Procedura shranjena.',
      'procedures.detail.stepsHeading': 'Koraki',
      'procedures.detail.addStep': 'Dodaj korak',
      'procedures.detail.stepAdded': 'Korak dodan.',
      'procedures.detail.editStep': 'Uredi',
      'procedures.detail.saveStep': 'Shrani',
      'procedures.detail.cancelEditStep': 'Prekliči',
      'procedures.detail.stepUpdated': 'Korak posodobljen.',
      'procedures.detail.removeStep': 'Odstrani',
      'procedures.detail.stepRemoved': 'Korak odstranjen.',
      'procedures.detail.stepsReordered': 'Koraki prerazporejeni.',
      'procedures.detail.moveUp': 'Premakni navzgor',
      'procedures.detail.moveDown': 'Premakni navzdol',
      'procedures.detail.addPhoto': 'Dodaj fotografijo',
      'procedures.detail.removePhoto': 'Odstrani fotografijo',
      'procedures.detail.photoAdded': 'Fotografija dodana.',
      'procedures.detail.photoRemoved': 'Fotografija odstranjena.',
      'procedures.detail.photoLimitReached': 'Doseženo je največje število — odstranite eno za dodajanje nove.',

      'admin.grants.title': 'Viri in dodelitve',
      'admin.grants.description': 'Vsi viri v sistemu in kdo ima do njih dodeljen dostop.',
      'admin.access.inspectDescription': 'Dejanski dostop, z razširjenimi podedovanimi in ekipnimi dodelitvami.',
      'admin.access.emailLabel': 'E-pošta uporabnika',
      'admin.access.teamLabel': 'Ekipa',
      'admin.access.resourceLabel': 'Vir',
      'admin.access.typeHeading': 'Vrsta',
      'admin.access.nameHeading': 'Vir',
      'admin.access.ownerHeading': 'Lastnik',
      'admin.access.grantsHeading': 'Dodelitve',
      'admin.access.actionsHeading': 'Dejanja',
      'admin.access.loading': 'Nalaganje virov',
      'admin.access.empty': 'Ni virov.',
      'admin.access.reload': 'Osveži',
      'admin.access.typeAsset': 'Sredstvo',
      'admin.access.typeBook': 'Knjiga',
      'admin.access.typeWorkspace': 'Delovni prostor',
      'admin.access.typeLocation': 'Lokacija',
      'admin.access.typeTaskType': 'Vrsta naloge',
      'admin.access.owner': 'Lastnik: {{owner}}',
      'admin.access.inWorkspace': 'V delovnem prostoru {{name}}',
      'admin.access.ownerDeleted': 'lastnik izbrisan',
      'admin.access.noGrants': 'Ni dodelitev.',
      'admin.access.grant': 'Dodeli dostop',
      'admin.access.grantTitle': 'Dodeli dostop do {{name}}',
      'admin.access.revoke': 'Odvzemi',
      'admin.access.kindUser': 'Uporabnik',
      'admin.access.kindTeam': 'Ekipa',
      'admin.access.emailPlaceholder': 'E-pošta uporabnika',
      'admin.access.noTeamsHint': 'Ni še ekip — ustvarite jo zgoraj.',
      'admin.access.permRead': 'Branje',
      'admin.access.permAdd': 'Dodajanje',
      'admin.access.permUpdate': 'Urejanje',
      'admin.access.permVerify': 'Potrjevanje',
      'admin.access.permDelete': 'Brisanje',
      'admin.access.permManage': 'Upravljanje',
      'admin.access.saveGrant': 'Dodeli',
      'admin.access.cancel': 'Prekliči',
      'admin.access.granted': 'Dostop dodeljen.',
      'admin.access.revoked': 'Dodelitev odvzeta.',
      'admin.access.inspectTitle': 'Pregled dostopa',
      'admin.access.inspectByUser': 'Po uporabniku',
      'admin.access.inspectByResource': 'Po viru',
      'admin.access.inspectRun': 'Poišči',
      'admin.access.inspectUserHeading': 'Dostop za {{email}}',
      'admin.access.inspectResourceHeading': 'Kdo lahko dostopa do {{name}}',
      'admin.access.inspectUserNotFound': 'Uporabnik s tem e-naslovom ne obstaja.',
      'admin.access.inspectNoRows': 'Ta uporabnik nima dostopa do nobenega vira.',
      'admin.access.deletedBadge': 'izbrisan',
      'admin.access.sourceOwner': 'Lastnik',
      'admin.access.sourceTeamOwner': 'V lasti ekipe »{{name}}«',
      'admin.access.sourceDirect': 'Neposredna dodelitev',
      'admin.access.sourceTeam': 'Prek ekipe »{{name}}«',
      'admin.access.sourceWorkspace': 'Podedovano iz delovnega prostora »{{name}}«',

      'nav.home': 'Domov',
      'nav.games': 'Igre',
      'nav.play': 'The Duke',
      'nav.activeGame': 'proti {{opponent}} — {{status}}',
      'nav.activeGame.yourTurn': 'na tebi je',
      'nav.activeGame.opponentTurn': 'na nasprotniku je',
      'nav.activeGame.waiting': 'čakanje',
      'nav.gameSetup': 'Postavitev plošče',
      'nav.gamePlay': 'Igraj igro',
      'nav.group.library': 'Knjižnica',
      'nav.group.workspace': 'Delovni prostor',
      'nav.group.admin': 'Skrbništvo',
      'nav.adminUsers': 'Uporabniki',
      'nav.adminAudit': 'Sled dejanj',
      'nav.adminInspector': 'Pregled dostopa',
      'nav.adminGrants': 'Viri in dodelitve',
      'nav.group.demo': 'Predstavitev',
      'nav.group.hidden': 'Skrito',
      'nav.books': 'Knjige',
      'nav.assets': 'Sredstva',
      'nav.asset': 'Sredstvo',
      'nav.job': 'Delo',
      'nav.workspaces': 'Delovni prostori',
      'nav.workspaceInvite': 'Povabilo v delovni prostor',
      'nav.teams': 'Ekipe',
      'nav.users': 'Osebe (demo)',
      'nav.invisible': 'Nevidno',
      'nav.unclickable': 'Neklikljivo',
      'nav.clickable': 'Klikljivo',
      'nav.expandable': 'Razširljivo Jure',
      'nav.child1': 'Otrok 1',
      'nav.child2': 'Otrok 2',
      'nav.both': 'Oboje',
      'nav.columns': 'Stolpci',
      'nav.settings': 'Nastavitve',
      'nav.advanced': 'Napredno',
      'nav.optionA': 'Možnost A',
      'nav.optionB': 'Možnost B',
      'nav.infraDemo': 'Predstavitev infrastrukture',
      'nav.adminErrors': 'Dnevnik napak',
      'nav.google': 'Google',
      'nav.up': 'Navigiraj gor',
      'nav.browseUp': 'Brskaj navzgor',
      'nav.backToCurrent': 'Nazaj na trenutno stran',
      'nav.section': 'Navigiraj razdelek',
      'nav.expand': 'Razširi',
      'nav.collapse': 'Strni',

      'shell.showNavigation': 'Prikaži navigacijo',
      'shell.hideNavigation': 'Skrij navigacijo',
      'shell.search': 'Iskanje',
      'shell.breadcrumb': 'Drobtinice',
      'shell.language': 'Jezik',
      'shell.adminMode': 'Skrbniški način',
      'shell.adminModeOn': 'Skrbniški način je vklopljen',
      'shell.adminModeOff': 'Skrbniški način je izklopljen',
      'workspace.switcher.label': 'Zamenjaj delovni prostor',
      'workspace.role.owner': 'Lastnik',
      'workspace.role.manager': 'Upravitelj',
      'workspace.role.member': 'Član',
      'workspace.role.guest': 'Gost',

      'commandPalette.pages': 'Strani',
      'commandPalette.empty': 'Ni zadetkov.',
      'commandPalette.quickAccess': 'Hitri dostop',
      'commandPalette.navigateHint': 'za premikanje',
      'commandPalette.selectHint': 'za izbiro',
      'commandPalette.closeHint': 'za zaprtje',
      'commandPalette.unread': 'Neprebrano',
      'commandPalette.external': 'Odpre se v novem zavihku',
      'commandPalette.searchResults': 'Rezultati iskanja',
      'commandPalette.searching': 'Iskanje …',

      'theme.toggleLabel': 'Tema: {{current}} (kliknite za preklop na {{next}})',
      'theme.light': 'Svetla',
      'theme.dark': 'Temna',
      'theme.system': 'Sistemska',

      'connectivity.offline': 'Niste povezani — nekatere stvari morda ne delujejo.',
      'connectivity.stale': 'Prikazani so podatki, naloženi ob {{time}}.',
      'connectivity.newVersion': 'Na voljo je nova različica.',

      'notFound.title': '404 — Strani ni mogoče najti',
      'notFound.body': 'Vneseni naslov se ne ujema z nobeno stranjo.',
      'notFound.goHome': 'Na začetno stran',

      'share.link.title': 'Odpiranje deljene naloge',
      'share.link.exchanging': 'Odpiranje naloge, ki je bila deljena z vami',
      'share.link.invalid': 'Ta povezava ni več veljavna',
      'share.link.invalidHelp':
        'Povezava za dostop je potekla ali bila preklicana. Prosite tistega, ki je delil nalogo, da pošlje novo.',
      'share.link.backToLogin': 'Pojdi na prijavo',

      'routeInfo.path': 'pot',
      'routeInfo.params': 'parametri',
      'routeInfo.query': 'poizvedba',

      'demo.title': 'Predstavitev infrastrukture',
      'demo.intro':
        'Igrišče za prečne mehanizme, ki nimajo lastnega naravnega sprožilca v vmesniku. (Teme, mobilna postavitev, preusmeritev nazaj po prijavi in vrstica napredka med navigacijo — glejte Settings, 1 s zakasnitve — se pokažejo že med običajno uporabo.)',

      'demo.notifications.title': 'Obvestila',
      'demo.notifications.info': 'Informacija',
      'demo.notifications.success': 'Uspeh',
      'demo.notifications.warning': 'Opozorilo',
      'demo.notifications.error': 'Napaka',
      'demo.notifications.sticky': 'Vztrajno (brez samodejnega skrivanja)',
      'demo.notifications.msgInfo': 'Tukaj je nekaj informacij.',
      'demo.notifications.msgSuccess': 'To je uspelo.',
      'demo.notifications.msgWarning': 'Previdno s tem.',
      'demo.notifications.msgError': 'To ni uspelo.',
      'demo.notifications.msgSticky': 'Vztrajno — zaprite me ročno.',

      'demo.gateways.title': 'Pomožni gateways',
      'demo.gateways.readClock': 'Preberi uro',
      'demo.gateways.generateId': 'Ustvari ID',
      'demo.gateways.rollRandom': 'Naključno število',

      'demo.draft.title': 'Shranjen osnutek (preživi osvežitev)',
      'demo.draft.placeholder': 'Nekaj natipkajte, nato osvežite stran.',

      'demo.dialog.title': 'Pogovorno okno',
      'demo.dialog.ask': 'Vprašaj za potrditev',
      'demo.dialog.lastResult': 'zadnji rezultat: {{result}}',
      'demo.dialog.question': 'Naj to storim?',
      'demo.dialog.confirmLabel': 'Stori',
      'demo.dialog.cancelLabel': 'Raje ne',
      'demo.dialog.confirmed': 'Potrjeno.',
      'demo.dialog.cancelled': 'Preklicano.',

      'demo.observability.title': 'Beleženje in telemetrija',
      'demo.observability.log': 'Zabeleži opozorilo',
      'demo.observability.track': 'Zabeleži dogodek',
      'demo.observability.logged': 'Zabeleženo — poglejte konzolo brskalnika.',
      'demo.observability.tracked': 'Dogodek zabeležen — poglejte konzolo brskalnika.',
      'demo.observability.note':
        'Pravega ponora še ni — oboje gre le v konzolo, a vsako klicno mesto že teče skozi gateway.',

      'demo.connectivity.title': 'Povezljivost in različica aplikacije',
      'demo.connectivity.goOffline': 'Simuliraj prekinitev povezave',
      'demo.connectivity.goOnline': 'Simuliraj ponovno povezavo',
      'demo.connectivity.online': 'povezan',
      'demo.connectivity.offline': 'brez povezave',
      'demo.connectivity.simulateVersion': 'Simuliraj novo razpoložljivo različico',
      'demo.connectivity.version': 'v{{version}}',
      'demo.connectivity.versionUpdate': 'v{{version}} (na voljo je posodobitev — glejte pasico)',

      'demo.i18n.title': 'i18n in oblikovanje',
      'demo.i18n.switchLocale': 'Zamenjaj jezik',

      'demo.scopedMessage.title': 'Sporočilo v obsegu (info / opozorilo)',
      'demo.scopedMessage.info': 'Pokaži informacijo',
      'demo.scopedMessage.warning': 'Pokaži opozorilo',
      'demo.scopedMessage.result': 'Pokaži izid akcije',
      'demo.scopedMessage.clear': 'Počisti',
      'demo.scopedMessage.msgInfo': 'Seznam je filtriran — prikazanih 3 od 12.',
      'demo.scopedMessage.msgWarning': 'Na tej strani imate neshranjene spremembe.',
      'demo.scopedMessage.msgFailed': 'Strežnik tega ni sprejel.',
      'demo.scopedMessage.note':
        'info in opozorilo se postavita eksplicitno prek show(), ker se ni zgodilo nič, iz česar bi lahko izpeljali ton. success in error lahko prideta le iz izida akcije — tretji gumb gre po izpeljani poti, da je razlika vidna.',

      'demo.unsaved.title': 'Opozorilo o neshranjenem delu',
      'demo.unsaved.placeholder': 'Nekaj natipkajte, nato poskusite osvežiti stran.',
      'demo.unsaved.save': 'Shrani',
      'demo.unsaved.dirty': 'neshranjeno',
      'demo.unsaved.clean': 'shranjeno',
      'demo.unsaved.note':
        'Dokler se to razlikuje od shranjene vrednosti, osvežitev ali zaprtje zavihka sproži vprašanje brskalnika — edini izhod, v katerega aplikacija sploh lahko poseže. Premik znotraj aplikacije ali gumb Nazaj sta dovoljena: zapisek živi v repozitoriju, zato je ob vrnitvi še vedno tu.',
      'demo.crash.title': 'Meja za sesutje',
      'demo.crash.button': 'Sesuj to stran',

      greeting: 'Pozdravljeni, {{name}}!',
      itemCount: '{{count}} predmetov',
    },
  }

  /** Storage and URLs are untrusted input — never widen a `string` to `Locale`. */
  isSupported(code: string): code is Locale {
    return this.locales.some((locale) => locale.code === code)
  }

  labelFor(code: Locale): string {
    return this.locales.find((locale) => locale.code === code)!.label
  }

  /** Next locale in declared order — what a cycle-through control walks. */
  nextAfter(code: Locale): Locale {
    const index = this.locales.findIndex((locale) => locale.code === code)
    return this.locales[(index + 1) % this.locales.length].code
  }
}
